import { useState, useEffect, useRef, useCallback, createContext, useContext } from "react";

/* ═══════════════════════════════════════════════════════════════
   THEME CONTEXT
═══════════════════════════════════════════════════════════════ */
const ThemeCtx = createContext<{ isDark: boolean; toggle: () => void }>({ isDark: false, toggle: () => {} });
const useTheme = () => useContext(ThemeCtx);

/* ═══════════════════════════════════════════════════════════════
   DESIGN TOKENS
═══════════════════════════════════════════════════════════════ */
const LIGHT_TOKENS = {
  bg:        "#ffffff",
  bg2:       "#f0f5ff",
  bg3:       "#e4ecfa",
  text:      "#0d1f3c",
  text2:     "#4a5e7a",
  text3:     "#7a91b0",
  card:      "#ffffff",
  cardBdr:   "#d8e4f5",
  shadow:    "0 4px 24px rgba(17,87,153,0.10)",
  shadowLg:  "0 12px 48px rgba(17,87,153,0.16)",
};
const DARK_TOKENS = {
  bg:        "#080f1e",
  bg2:       "#0c1628",
  bg3:       "#111e35",
  text:      "#e8f0ff",
  text2:     "#8fa8cc",
  text3:     "#5a7499",
  card:      "#0e1a2e",
  cardBdr:   "#1a2d4a",
  shadow:    "0 4px 24px rgba(0,0,0,0.40)",
  shadowLg:  "0 12px 48px rgba(0,0,0,0.50)",
};

const BRAND = {
  primary:  "#115799",
  primaryD: "#0a3f72",
  primaryL: "#1a6db5",
  sky:      "#2d9cdb",
  skyL:     "#56b8e8",
  accent:   "#f5a623",
  accentD:  "#d8881a",
  navy:     "#060e1d",
  navyMid:  "#0c1a33",
};

const gradHero    = `linear-gradient(145deg,${BRAND.navy} 0%,#0b2148 45%,${BRAND.primary} 100%)`;
const gradPrimary = `linear-gradient(135deg,${BRAND.primary} 0%,${BRAND.sky} 100%)`;
const gradAccent  = `linear-gradient(135deg,${BRAND.accent} 0%,#f9c55a 100%)`;

/* ═══════════════════════════════════════════════════════════════
   GLOBAL CSS
═══════════════════════════════════════════════════════════════ */
const GLOBAL_CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Open+Sans:ital,wght@0,300;0,400;0,600;0,700;0,800;1,400&display=swap');
  *,*::before,*::after{box-sizing:border-box;margin:0;padding:0}
  html{scroll-behavior:smooth;-webkit-font-smoothing:antialiased}
  body{font-family:'Open Sans',sans-serif;overflow-x:hidden;transition:background 0.3s,color 0.3s}
  a{text-decoration:none}
  button{font-family:inherit;cursor:pointer;border:none;background:none}
  ::-webkit-scrollbar{width:5px}
  ::-webkit-scrollbar-track{background:#060e1d}
  ::-webkit-scrollbar-thumb{background:#115799;border-radius:3px}

  .fi{opacity:0;transform:translateY(22px);transition:opacity .55s ease,transform .55s ease}
  .fi.vis{opacity:1;transform:translateY(0)}

  /* Layout */
  .wrap{max-width:1160px;margin:0 auto;width:100%}
  .sec{padding:88px clamp(20px,6vw,80px)}
  .sec-inner{max-width:1160px;margin:0 auto}

  /* Cards */
  .card-hover{transition:transform .22s ease,box-shadow .22s ease}
  .card-hover:hover{transform:translateY(-4px)}

  /* Buttons */
  .btn{display:inline-flex;align-items:center;justify-content:center;gap:8px;font-weight:700;border-radius:10px;cursor:pointer;transition:transform .15s,box-shadow .15s;font-family:'Open Sans',sans-serif;text-decoration:none;border:none}
  .btn:hover{transform:translateY(-2px)}
  .btn-primary{background:linear-gradient(135deg,#f5a623,#f9c55a);color:#060e1d;box-shadow:0 6px 22px rgba(245,166,35,.38)}
  .btn-primary:hover{box-shadow:0 10px 32px rgba(245,166,35,.52)}
  .btn-ghost{background:rgba(255,255,255,.1);color:#fff;border:1.5px solid rgba(255,255,255,.22)}
  .btn-ghost:hover{background:rgba(255,255,255,.18)}
  .btn-outline{background:transparent;color:#115799;border:2px solid #115799}
  .btn-outline:hover{background:#115799;color:#fff}
  .btn-lg{font-size:15px;padding:14px 30px}
  .btn-md{font-size:13px;padding:11px 24px}
  .btn-sm{font-size:12px;padding:8px 18px}

  .chip{font-size:10px;font-weight:700;padding:3px 10px;border-radius:20px;display:inline-block;margin:2px 2px 2px 0;background:#eef4fc;color:#115799}

  /* Animations */
  @keyframes float  {0%,100%{transform:translateY(0)}50%{transform:translateY(-9px)}}
  @keyframes floatB {0%,100%{transform:translateY(0)}50%{transform:translateY(-5px)}}
  @keyframes pulse  {0%,100%{opacity:1}50%{opacity:.35}}
  @keyframes dash   {to{stroke-dashoffset:-24}}
  @keyframes spinCW {to{transform:rotate(360deg)}}
  @keyframes spinCCW{to{transform:rotate(-360deg)}}
  @keyframes slideUp{from{opacity:0;transform:translateY(18px)}to{opacity:1;transform:translateY(0)}}

  /* Nav */
  .nav-lnk{position:relative;transition:color .2s}
  .nav-lnk.active{color:#fff!important}
  .nav-lnk.active::after{content:'';position:absolute;bottom:-4px;left:50%;transform:translateX(-50%);width:18px;height:2px;background:#f5a623;border-radius:2px}

  /* Form inputs */
  .form-input{width:100%;padding:13px 16px;border:1.5px solid #d0ddf0;border-radius:10px;font-size:14px;transition:border .2s,box-shadow .2s;outline:none;font-family:'Open Sans',sans-serif}
  .form-input:focus{border-color:#115799;box-shadow:0 0 0 3px rgba(17,87,153,.1)}
  .form-input::placeholder{color:#aab5c8}
  textarea.form-input{resize:vertical;min-height:130px}
  select.form-input{appearance:none;background-image:url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath d='M1 1l5 5 5-5' stroke='%23607898' stroke-width='1.5' fill='none' stroke-linecap='round'/%3E%3C/svg%3E");background-repeat:no-repeat;background-position:right 14px center;padding-right:38px}

  /* FAQ */
  .faq-a{max-height:0;overflow:hidden;transition:max-height .35s ease,padding .35s ease}
  .faq-open .faq-a{max-height:400px;padding-bottom:20px}
  .faq-icon{transition:transform .25s}
  .faq-open .faq-icon{transform:rotate(45deg)}

  /* Modal */
  .modal-overlay{opacity:0;pointer-events:none;transition:opacity .25s}
  .modal-overlay.open{opacity:1;pointer-events:all}
  .modal-box{transform:scale(0.94) translateY(16px);transition:transform .25s}
  .modal-overlay.open .modal-box{transform:scale(1) translateY(0)}

  /* Prod card open hint */
  .prod-card{cursor:pointer}
  .prod-open-hint{font-size:10px;font-weight:600;margin-top:10px;transition:color .2s}

  /* Responsive */
  @media(max-width:900px){
    .desk-links{display:none!important}
    .mob-ham{display:flex!important}
    .mob-menu.open{display:flex!important}
    .iso-panel{display:none!important}
    .stats-bar{grid-template-columns:1fr 1fr!important}
    .footer-grid{grid-template-columns:1fr 1fr!important}
  }
  @media(max-width:600px){
    .two-col{grid-template-columns:1fr!important}
    .three-col{grid-template-columns:1fr!important}
    .hero-row{flex-direction:column!important}
  }
`;

/* ═══════════════════════════════════════════════════════════════
   HOOKS
═══════════════════════════════════════════════════════════════ */
function useFI(threshold = 0.08) {
  const ref = useRef<HTMLDivElement>(null);
  const [vis, setVis] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) { setVis(true); obs.disconnect(); }
    }, { threshold });
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, vis] as const;
}

function FI({ children, delay = 0, style = {}, className = "" }: {
  children: React.ReactNode; delay?: number; style?: React.CSSProperties; className?: string;
}) {
  const [ref, vis] = useFI();
  return (
    <div ref={ref} className={`fi${vis ? " vis" : ""} ${className}`}
      style={{ transitionDelay: `${delay}s`, ...style }}>
      {children}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   ATOMS
═══════════════════════════════════════════════════════════════ */
function useT() {
  const { isDark } = useTheme();
  return isDark ? DARK_TOKENS : LIGHT_TOKENS;
}

function Eyebrow({ c, onDark }: { c: string; onDark?: boolean }) {
  return (
    <p style={{ fontSize: 10, fontWeight: 800, letterSpacing: "4px", textTransform: "uppercase",
      color: onDark ? BRAND.accent : BRAND.primary, marginBottom: 10 }}>{c}</p>
  );
}
function SH2({ children, onDark, accent }: { children: React.ReactNode; onDark?: boolean; accent?: boolean }) {
  const t = useT();
  return (
    <h2 style={{ fontFamily: "'Open Sans',sans-serif", fontSize: "clamp(26px,4.5vw,42px)", fontWeight: 800,
      lineHeight: 1.1, letterSpacing: "-1px", marginBottom: 14,
      color: onDark ? "#fff" : t.text }}>
      {children}
    </h2>
  );
}
function Lead({ children, onDark }: { children: React.ReactNode; onDark?: boolean }) {
  const t = useT();
  return (
    <p style={{ fontSize: 16, lineHeight: 1.82, maxWidth: 720, marginBottom: 40,
      color: onDark ? "rgba(255,255,255,.65)" : t.text2 }}>{children}</p>
  );
}

function Btn({ children, v = "primary", size = "md", onClick, href, style = {}, type, disabled }: any) {
  const El: any = href ? "a" : "button";
  return (
    <El href={href} onClick={onClick} type={type} disabled={disabled}
      className={`btn btn-${v} btn-${size}`} style={style}>
      {children}
    </El>
  );
}

/* ═══════════════════════════════════════════════════════════════
   PRODUCT DATA
═══════════════════════════════════════════════════════════════ */
interface Product {
  id: string; tag: string; name: string; col: string; tags: string[]; desc: string; icon: string;
  detail: {
    headline: string; summary: string;
    features: { icon: string; t: string; d: string }[];
    clients: string[];
    quote: { text: string; attr: string };
  };
}

const PRODUCTS: Product[] = [
  { id:"xpressq", tag:"Queue Management", name:"XpressQ", col:"#115799", icon:"🔢",
    tags:["WhatsApp","Web","On-site Kiosk"],
    desc:"Africa's first WhatsApp queuing system. Virtual & on-site queue management.",
    detail:{ headline:"Africa's First WhatsApp Queue System",
      summary:"XpressQ revolutionises queue management by letting customers join a queue via WhatsApp — the platform 90% of Africans already use daily. No app download required.",
      features:[
        {icon:"💬",t:"WhatsApp Integration",d:"Customers join queues via WhatsApp — meeting them where they already are. No app install, no friction."},
        {icon:"🖥️",t:"On-Site Kiosk Check-in",d:"Physical kiosks at branches allow walk-in customers to join digitally and receive a ticket with real-time wait estimate."},
        {icon:"📊",t:"Live Queue Dashboard",d:"Branch managers see live queue length, average wait times, and service agent performance in real time."},
        {icon:"🔔",t:"Smart Notifications",d:"Customers receive WhatsApp updates as they approach the front of the queue — eliminating physical waiting."},
        {icon:"📈",t:"Analytics & Reporting",d:"Historical data on peak times, wait durations, and agent throughput to optimise staffing and scheduling."},
        {icon:"🔗",t:"API Integration",d:"Connects to your existing CRM or core banking system — customer data surfaced to agents before the interaction starts."},
      ],
      clients:["Nedbank Namibia ", "Swakopmund Municipality","Keetmanshoop Municipality"],
      quote:{text:"Within 4 months, residents felt valued — knowing their expected wait time from the moment they arrived.",attr:"Swakopmund Municipality"},
    }},
  { id:"selfservice", tag:"Self-Service", name:"Xpress Self-Service", col:"#2d9cdb", icon:"🤖",
    tags:["Kiosk","Web"],
    desc:"Customers serve themselves — reducing queues, cutting costs, increasing revenue.",
    detail:{ headline:"Reduce Queues. Cut Costs. Grow Revenue.",
      summary:"Xpress Self-Service kiosks empower customers to complete transactions independently — freeing your frontline staff for complex, high-value interactions.",
      features:[
        {icon:"💳",t:"Multi-Transaction Support",d:"Handle payments, account queries, form submissions, and more — all without a human agent."},
        {icon:"🏪",t:"Revenue Generation",d:"Upsell and cross-sell products on-screen during transactions — turning a service touchpoint into a sales channel."},
        {icon:"🌍",t:"Multi-Language UI",d:"Support for multiple local languages ensures inclusivity and reduces language-barrier drop-offs."},
        {icon:"🔒",t:"Secure & Compliant",d:"End-to-end encrypted transactions with audit logs and compliance-ready reporting."},
        {icon:"📊",t:"Usage Analytics",d:"Real-time dashboards showing transaction volumes, peak usage times, and popular services."},
        {icon:"⚙️",t:"Remote Management",d:"Update kiosk content, pricing, and workflows remotely — no on-site IT visits."},
      ],
      clients:[ "Keetmanshoop Municipality","Nored"],
      quote:{text:"After 18 months, the municipality recorded increased revenue through self-service transactions and gained meaningful data for planning.",attr:"Keetmanshoop Municipality"},
    }},
  { id:"signage", tag:"Digital Signage", name:"Xpress Signage", col:"#7c4dff", icon:"📺",
    tags:["Display Screens"],
    desc:"Centrally managed digital signage for queue status, content and promotions.",
    detail:{ headline:"Every Screen, One Dashboard",
      summary:"Xpress Signage gives you centralised control over every digital display across all your locations — from queue status boards to promotional content and emergency alerts.",
      features:[
        {icon:"🗂️",t:"Playlist Management",d:"Create and schedule content playlists by location, time of day, or audience — all from one dashboard."},
        {icon:"🔢",t:"Live Queue Integration",d:"Screens automatically display real-time queue numbers, counter assignments, and estimated wait times."},
        {icon:"📢",t:"Emergency Broadcasts",d:"Push urgent messages instantly to all screens across all locations with a single click."},
        {icon:"🎬",t:"Rich Media Support",d:"Videos, images, animations, live data feeds — display any content format with pixel-perfect rendering."},
        {icon:"📍",t:"Location Targeting",d:"Send different content to different branches, regions, or even individual screens based on context."},
        {icon:"📊",t:"Proof of Play Reporting",d:"Audit logs confirming what content played, on which screen, and for how long."},
      ],
      clients:["Nedbank Namibia",  "Triumpat College", "Swakop Municipality", "Ministry of Justice", "Keetmanshoop Municipality"],
      quote:{text:"Our branch displays now serve both queue management and promotional content — one system, zero duplication.",attr:"Financial Sector Client"},
    }},
  { id:"feedback", tag:"Customer Feedback", name:"Xpress Feedback", col:"#f5a623", icon:"⭐",
    tags:["Email","SMS","WhatsApp","Online","Tablet"],
    desc:"Multi-channel feedback collection across every touchpoint.",
    detail:{ headline:"Hear Every Customer. Everywhere.",
      summary:"Xpress Feedback collects structured customer experience data across every channel — so you always know how your organisation is performing, in real time.",
      features:[
        {icon:"📱",t:"Multi-Channel Collection",d:"Collect feedback via WhatsApp, SMS, email, web forms, tablet kiosks, and QR codes — wherever your customers are."},
        {icon:"⏱️",t:"Real-Time Alerts",d:"Negative feedback triggers instant alerts to managers — enabling immediate service recovery before the customer leaves."},
        {icon:"📊",t:"NPS & CSAT Tracking",d:"Track industry-standard metrics over time to benchmark and improve customer satisfaction."},
        {icon:"🏷️",t:"Smart Tagging & Routing",d:"Responses are automatically categorised and routed to the right department for follow-up."},
        {icon:"📈",t:"Trend Analysis",d:"Spot recurring issues, seasonal patterns, and branch-level performance gaps with built-in analytics."},
        {icon:"🔗",t:"CRM Integration",d:"Link feedback to specific customer records and interaction history for a complete view of each relationship."},
      ],
      clients:["Ministry of Justice","BIPA","Triumphant College"],
      quote:{text:"We now act on feedback the same day it's received. The real-time alert system has transformed our complaint resolution.",attr:"Government Ministry Client"},
    }},
  { id:"chat", tag:"Agent Collaboration", name:"Xpress Chat", col:"#00bfa5", icon:"💬",
    tags:["Agent Desktop"],
    desc:"Internal messaging for frontline and back-office service coordination.",
    detail:{ headline:"Connect Every Agent. Solve Every Ticket.",
      summary:"Xpress Chat enables real-time internal communication between frontline staff and back-office teams — speeding up resolution, reducing customer wait times.",
      features:[
        {icon:"🔄",t:"Instant Escalation",d:"Frontline agents escalate cases to specialists in one click — with full customer context automatically shared."},
        {icon:"👥",t:"Team Channels",d:"Create topic-specific channels for departments, branches, or projects — keeping communication organised."},
        {icon:"📎",t:"File & Document Sharing",d:"Share forms, IDs, documents, and images securely within the platform."},
        {icon:"🔔",t:"Smart Notifications",d:"Configurable notification rules ensure agents are alerted only for what matters."},
        {icon:"📊",t:"Communication Analytics",d:"Track response times, escalation rates, and collaboration patterns to identify coaching opportunities."},
        {icon:"🔒",t:"Audit Trail",d:"Every message is logged and searchable — essential for compliance, disputes, and quality assurance."},
      ],
      clients:["Nedbank Namibia" ],
      quote:{text:"Response times dropped significantly once agents could instantly reach the right person internally without picking up the phone.",attr:"Telecoms Client"},
    }},
  { id:"visit", tag:"Visitor Management", name:"Xpress Visit", col:"#e91e63", icon:"🏷️",
    tags:["On-site"],
    desc:"Digital visitor registration, tracking, and full records management.",
    detail:{ headline:"Know Who's In Your Building. Always.",
      summary:"Xpress Visit replaces paper visitor books with a secure, digital visitor management system — providing full audit trails, pre-registration, and host notifications.",
      features:[
        {icon:"📋",t:"Digital Sign-In",d:"Visitors sign in on a tablet kiosk — capturing name, purpose, host, and ID details instantly and accurately."},
        {icon:"📧",t:"Host Notifications",d:"Hosts receive an instant notification when their visitor arrives — no reception calls needed."},
        {icon:"🪪",t:"Visitor Badges",d:"Print branded visitor badges on arrival — including photo, time, and access level."},
        {icon:"📅",t:"Pre-Registration",d:"Hosts pre-register expected visitors so sign-in takes seconds — improving the first impression."},
        {icon:"📊",t:"Occupancy Reporting",d:"Real-time view of who is on the premises at any given time — essential for safety and emergency procedures."},
        {icon:"🔒",t:"Compliance & Privacy",d:"GDPR-compliant data handling with configurable retention policies and secure encrypted storage."},
      ],
      clients:["Ministry of Justice", ],
      quote:{text:"Compliance audits now take minutes instead of days — every visitor record is instantly searchable and accurately timestamped.",attr:"Government Client"},
    }},
  { id:"forms", tag:"Digital Forms", name:"Xpress Forms", col:"#ff6f00", icon:"📝",
    tags:["Any Device"],
    desc:"Replace paper with a digital form builder — capture, store, and analyse data.",
    detail:{ headline:"Kill Paper. Unlock Data.",
      summary:"Xpress Forms eliminates paper-based data capture — replacing it with smart, conditional digital forms that can be completed on any device and analysed instantly.",
      features:[
        {icon:"🧱",t:"Drag-and-Drop Builder",d:"Create any form without coding — text fields, dropdowns, signatures, file uploads, and conditional logic."},
        {icon:"📱",t:"Any Device, Offline Too",d:"Forms work on tablets, phones, and desktops — with offline capability for field-based data capture."},
        {icon:"🔀",t:"Conditional Logic",d:"Show or hide fields based on previous answers — creating smart, dynamic forms that guide the user."},
        {icon:"📊",t:"Instant Analytics",d:"Submitted data is immediately available for analysis — no manual data entry, no transcription errors."},
        {icon:"🔗",t:"System Integration",d:"Push form data directly to your CRM, ERP, or database via API — eliminating double-entry."},
        {icon:"✍️",t:"Digital Signatures",d:"Capture legally-binding digital signatures on-screen — reducing the need for printing and scanning."},
      ],
      clients:["Ministry of Justice","EIF"],
      quote:{text:"We eliminated over 40 paper-based processes in year one. The time saved is extraordinary.",attr:"Government Ministry"},
    }},
  { id:"bi", tag:"Business Intelligence", name:"Xpress BI", col:"#115799", icon:"📊",
    tags:["Dashboard","Reports"],
    desc:"Real-time analytics for faster, smarter, evidence-based decisions.",
    detail:{ headline:"Real-Time Intelligence. Actionable Insights.",
      summary:"Xpress BI aggregates data from across the entire Xpress CX Suite into a single, powerful analytics platform — giving managers and executives the clarity to act fast.",
      features:[
        {icon:"📈",t:"Live Dashboards",d:"Real-time KPI dashboards customised for each role — branch managers, regional heads, and executives see what they need."},
        {icon:"🗓️",t:"Scheduled Reports",d:"Automate daily, weekly, and monthly reports delivered to email — no manual data pulling."},
        {icon:"🔍",t:"Drill-Down Analysis",d:"Click into any metric to see the underlying data — by branch, date, agent, or service type."},
        {icon:"🌐",t:"Multi-Source Integration",d:"Pull data from XpressQ, Self-Service, Feedback, and other modules into unified views."},
        {icon:"⚡",t:"Predictive Insights",d:"Identify patterns and trends before they become problems — proactive staffing, stock, and service planning."},
        {icon:"📤",t:"Export & Share",d:"Export reports to PDF, Excel, or CSV — or share live dashboard links with stakeholders."},
      ],
      clients:["Nedbank Namibia", "Keetmanshoop Municipality"],
      quote:{text:"For the first time, we could see what was actually happening across all branches — in one view, updated in real time.",attr:"Nedbank Namibia"},
    }},
  { id:"events", tag:"Event Management", name:"Xpress Events", col:"#2d9cdb", icon:"🎪",
    tags:["Online","On-site"],
    desc:"End-to-end event management from registrations to post-event analytics.",
    detail:{ headline:"Run Seamless Events. Capture Every Insight.",
      summary:"Xpress Events handles the full event lifecycle — from online registration and ticketing to on-site check-in, live engagement, and post-event reporting.",
      features:[
        {icon:"🎟️",t:"Online Registration & Ticketing",d:"Branded registration pages with paid or free ticketing, confirmation emails, and QR code tickets."},
        {icon:"📲",t:"On-Site QR Check-In",d:"Fast QR code scanning at the door — eliminating queues and providing real-time attendance tracking."},
        {icon:"🗺️",t:"Event Agenda Builder",d:"Create and publish event schedules, speaker profiles, and session details in one platform."},
        {icon:"📊",t:"Live Attendance Dashboard",d:"See who has checked in, how full each session is, and where bottlenecks are forming — in real time."},
        {icon:"💬",t:"Attendee Engagement",d:"Live polls, Q&A submissions, and feedback forms keep attendees engaged and generate actionable data."},
        {icon:"📈",t:"Post-Event Analytics",d:"Comprehensive reports on attendance, engagement, satisfaction, and ROI delivered after the event."},
      ],
      clients:["Opuwo Town Council","Triumphant College"],
      quote:{text:"Our annual conference registration went from spreadsheets and phone calls to a fully automated system. Night and day difference.",attr:"Annual Conference Organiser"},
    }},
  { id:"crm", tag:"CRM", name:"Xpress CRM", col:"#7c4dff", icon:"👥",
    tags:["Integrated"],
    desc:"Manage customer records and interactions within the same CX ecosystem.",
    detail:{ headline:"Every Customer Relationship, In One Place.",
      summary:"Xpress CRM connects all your customer touchpoints into a single profile — so every agent has the full picture before they say hello.",
      features:[
        {icon:"📁",t:"360° Customer Profiles",d:"View every interaction, transaction, feedback submission, and service history in one unified customer record."},
        {icon:"🔔",t:"Interaction Tracking",d:"Every call, visit, queue ticket, and feedback response is logged automatically — zero manual entry."},
        {icon:"🎯",t:"Customer Segmentation",d:"Create dynamic customer segments based on behaviour, value, or demographics for targeted communications."},
        {icon:"⚡",t:"Automated Follow-Ups",d:"Set triggers to automatically send follow-up messages, reminders, or offers based on customer actions."},
        {icon:"📊",t:"Pipeline Management",d:"Track sales opportunities and service cases through configurable pipelines with stage-based accountability."},
        {icon:"🔗",t:"Suite Integration",d:"Automatically pulls data from XpressQ, Feedback, Forms, and all other modules — no duplicate entry."},
      ],
      clients:["Nedbank Namibia" ],
      quote:{text:"Having queue history, feedback, and CRM data in one view transformed how our agents prepare for each interaction.",attr:"Banking Client"},
    }},
  { id:"message", tag:"Mass Messaging", name:"Xpress Message", col:"#00bfa5", icon:"📨",
    tags:["Email","SMS","WhatsApp"],
    desc:"Reach customers at scale via email, SMS, and WhatsApp.",
    detail:{ headline:"Reach Everyone. Miss No One.",
      summary:"Xpress Message enables mass and targeted outbound communication via email, SMS, and WhatsApp — from transactional notifications to marketing campaigns.",
      features:[
        {icon:"📧",t:"Multi-Channel Campaigns",d:"Send the same campaign across email, SMS, and WhatsApp simultaneously — or choose the best channel per segment."},
        {icon:"🎨",t:"Drag-and-Drop Editor",d:"Create professional, branded email templates without coding — responsive on all devices."},
        {icon:"🎯",t:"Audience Segmentation",d:"Target specific customer groups based on CRM data, behaviour, location, or custom criteria."},
        {icon:"⏰",t:"Scheduled Sending",d:"Schedule campaigns to send at optimal times — or set recurring reminders and notifications."},
        {icon:"📊",t:"Delivery Analytics",d:"Track open rates, click-throughs, delivery rates, and opt-outs in real time for each campaign."},
        {icon:"🤖",t:"Automated Journeys",d:"Set up trigger-based message flows — appointment reminders, welcome sequences, and re-engagement campaigns."},
      ],
      clients:["Triumphant College"],
      quote:{text:"Our WhatsApp campaign reached 12,000 customers in under an hour with a 78% open rate.",attr:"Education Client"},
    }},
];

/* ═══════════════════════════════════════════════════════════════
   PRODUCT MODAL
═══════════════════════════════════════════════════════════════ */
function ProductModal({ prod, onClose }: { prod: Product | null; onClose: () => void }) {
  const t = useT();
 
  useEffect(() => {
    if (prod) document.body.style.overflow = "hidden";
    else document.body.style.overflow = "";
    return () => { document.body.style.overflow = ""; };
  }, [prod]);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose]);

  return (
    <div
      className={`modal-overlay${prod ? " open" : ""}`}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      style={{
        position: "fixed", inset: 0, background: "rgba(6,14,29,0.85)",
        zIndex: 800, display: "flex", alignItems: "center", justifyContent: "center",
        padding: 20, backdropFilter: "blur(8px)",
      }}
    >
      {prod && (
        <div className="modal-box" style={{
          background: t.card, borderRadius: 20, maxWidth: 680, width: "100%",
          maxHeight: "90vh", overflowY: "auto", position: "relative",
          border: `1px solid ${t.cardBdr}`, boxShadow: t.shadowLg,
        }}>
          {/* header */}
          <div style={{
            padding: "32px 36px 24px", borderBottom: `1px solid ${t.cardBdr}`,
            position: "sticky", top: 0, background: t.card, borderRadius: "20px 20px 0 0", zIndex: 1,
          }}>
            <button onClick={onClose} style={{
              position: "absolute", top: 18, right: 18, width: 34, height: 34,
              borderRadius: "50%", background: t.bg2, border: `1px solid ${t.cardBdr}`,
              color: t.text, fontSize: 16, display: "flex", alignItems: "center", justifyContent: "center",
              cursor: "pointer", transition: "background .2s",
            }}>✕</button>
            <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 12 }}>
              <div style={{
                width: 52, height: 52, borderRadius: 13, background: prod.col,
                display: "flex", alignItems: "center", justifyContent: "center", fontSize: 24, flexShrink: 0,
              }}>{prod.icon}</div>
              <div>
                <div style={{ fontSize: 9, fontWeight: 800, letterSpacing: "3px", textTransform: "uppercase", color: prod.col, marginBottom: 4 }}>{prod.tag}</div>
                <h2 style={{ fontFamily: "'Open Sans',sans-serif", fontSize: 22, fontWeight: 800, color: t.text, letterSpacing: "-0.5px" }}>{prod.name}</h2>
              </div>
            </div>
            <p style={{ fontSize: 15, fontWeight: 600, color: t.text2, lineHeight: 1.6 }}>{prod.detail.headline}</p>
          </div>
          {/* body */}
          <div style={{ padding: "28px 36px 36px" }}>
            <p style={{ fontSize: 14, color: t.text2, lineHeight: 1.78, marginBottom: 24 }}>{prod.detail.summary}</p>
            <h3 style={{ fontFamily: "'Open Sans',sans-serif", fontSize: 16, fontWeight: 700, color: t.text, marginBottom: 16 }}>Key Features</h3>
            {prod.detail.features.map((f, i) => (
              <div key={i} style={{
                display: "flex", gap: 14, alignItems: "flex-start", marginBottom: 16,
                padding: 16, background: t.bg2, borderRadius: 10,
              }}>
                <div style={{
                  fontSize: 22, flexShrink: 0, width: 42, height: 42,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  borderRadius: 10, background: t.card,
                }}>{f.icon}</div>
                <div>
                  <h4 style={{ fontSize: 14, fontWeight: 700, color: t.text, marginBottom: 4 }}>{f.t}</h4>
                  <p style={{ fontSize: 13, color: t.text2, lineHeight: 1.65 }}>{f.d}</p>
                </div>
              </div>
            ))}
            <div style={{ marginTop: 24, padding: 20, background: t.bg2, borderRadius: 12, borderLeft: `4px solid ${prod.col}` }}>
              <p style={{ fontSize: 14, fontStyle: "italic", color: t.text2, lineHeight: 1.75, marginBottom: 8 }}>"{prod.detail.quote.text}"</p>
              <p style={{ fontSize: 11, fontWeight: 700, color: BRAND.primary }}>— {prod.detail.quote.attr}</p>
            </div>
            <div style={{ marginTop: 20 }}>
              <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: "2px", textTransform: "uppercase", color: t.text3, marginBottom: 10 }}>Used By</div>
              <div>{prod.detail.clients.map(c => <span key={c} className="chip" style={{ background: t.bg2, color: prod.col }}>{c}</span>)}</div>
            </div>
            <div style={{ marginTop: 24 }}>
              <Btn v="primary" size="md" style={{ width: "100%" }}>Request a Demo of {prod.name} →</Btn>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   ISO ILLUSTRATION
═══════════════════════════════════════════════════════════════ */
function IsoIllustration() {
  const cx = 260, cy = 228;
  const modules = [
    { x:65,  y:70,  col:"#128C7E", colD:"#075E54", icon:"💬", lbl:"XpressQ",      anim:"float 4s ease-in-out 0s infinite" },
    { x:330, y:44,  col:"#f5a623", colD:"#d8881a", icon:"⭐", lbl:"Feedback",     anim:"float 3.8s ease-in-out 0.3s infinite" },
    { x:18,  y:178, col:"#2d9cdb", colD:"#1a7ab5", icon:"🤖", lbl:"Self-Service", anim:"float 4.4s ease-in-out 0.6s infinite" },
    { x:420, y:166, col:"#7c4dff", colD:"#5730d6", icon:"📺", lbl:"Signage",      anim:"floatB 4s ease-in-out 0.2s infinite" },
    { x:40,  y:296, col:"#e91e63", colD:"#ad1457", icon:"🏷️", lbl:"Visit",        anim:"float 5s ease-in-out 0.9s infinite" },
    { x:396, y:287, col:"#ff6f00", colD:"#cc5800", icon:"📨", lbl:"Message",      anim:"floatB 4.2s ease-in-out 0.7s infinite" },
    { x:188, y:352, col:"#115799", colD:"#0a3f72", icon:"📊", lbl:"Xpress BI",    anim:"float 3.6s ease-in-out 1.1s infinite" },
  ];
  return (
    <svg viewBox="0 0 520 440" width="100%" style={{ overflow: "visible" }} xmlns="http://www.w3.org/2000/svg">
      {/* glow */}
      <circle cx={cx} cy={cy} r={170} fill="rgba(17,87,153,0.06)" />
      <circle cx={cx} cy={cy} r={120} fill="rgba(45,156,219,0.05)" />
      {/* rings */}
      <circle cx={cx} cy={cy} r={132} fill="none" stroke={BRAND.accent} strokeWidth={1} strokeOpacity={0.18} strokeDasharray="6 6"
        style={{ animation: "spinCW 20s linear infinite", transformOrigin: `${cx}px ${cy}px` }} />
      <circle cx={cx} cy={cy} r={160} fill="none" stroke={BRAND.primary} strokeWidth={0.6} strokeOpacity={0.12} strokeDasharray="3 9"
        style={{ animation: "spinCCW 32s linear infinite", transformOrigin: `${cx}px ${cy}px` }} />
      {/* connection lines */}
      {modules.map((m, i) => (
        <line key={i} x1={m.x + 50} y1={m.y + 20} x2={cx} y2={cy}
          stroke={m.col} strokeWidth={0.9} strokeOpacity={0.28} strokeDasharray="5 4"
          style={{ animation: `dash ${2.2 + i * 0.25}s linear ${i * 0.12}s infinite` }} />
      ))}
      {/* ambient dots */}
      {[[460,90],[30,140],[490,350],[140,420],[400,420]].map(([dx,dy],i) => (
        <circle key={i} cx={dx} cy={dy} r={2.5} fill="rgba(255,255,255,0.2)"
          style={{ animation: `pulse ${1.8 + i * 0.4}s ease-in-out ${i * 0.25}s infinite` }} />
      ))}
      {/* module tiles */}
      {modules.map((m, i) => (
        <g key={i} style={{ animation: m.anim }}>
          <rect x={m.x} y={m.y} width={84} height={56} rx={10} fill={m.col} />
          <rect x={m.x} y={m.y} width={84} height={20} rx={10} fill={m.colD} />
          <rect x={m.x} y={m.y + 6} width={84} height={14} fill={m.colD} />
          <text x={m.x + 42} y={m.y + 16} textAnchor="middle" fontSize={7.5} fill="rgba(255,255,255,0.8)" fontFamily="Open Sans,sans-serif" fontWeight="700">{m.lbl.toUpperCase()}</text>
          <text x={m.x + 42} y={m.y + 38} textAnchor="middle" fontSize={16} fill="white" fontFamily="Open Sans,sans-serif">{m.icon}</text>
          <text x={m.x + 42} y={m.y + 52} textAnchor="middle" fontSize={7.5} fill="rgba(255,255,255,0.7)" fontFamily="Open Sans,sans-serif">{m.lbl}</text>
        </g>
      ))}
      {/* center hub */}
      <g style={{ animation: "float 5.5s ease-in-out 0s infinite" }}>
        <circle cx={cx} cy={cy} r={52} fill="rgba(17,87,153,0.25)" />
        <circle cx={cx} cy={cy} r={44} fill="rgba(17,87,153,0.35)" />
        <circle cx={cx} cy={cy} r={38} fill="#115799" />
        <circle cx={cx} cy={cy} r={35} fill="#1a6db5" />
        <text x={cx} y={cy - 6} textAnchor="middle" fontSize={22} fill="white" fontFamily="Open Sans,sans-serif" fontWeight="900">∞</text>
        <text x={cx} y={cy + 10} textAnchor="middle" fontSize={8} fill="rgba(255,255,255,0.9)" fontFamily="Open Sans,sans-serif" fontWeight="800" letterSpacing="1">PLATFORM HUB</text>
        <circle cx={cx} cy={cy} r={38} fill="none" stroke="rgba(255,255,255,0.25)" strokeWidth={1.5} />
      </g>
    </svg>
  );
}

/* ═══════════════════════════════════════════════════════════════
   NAV
═══════════════════════════════════════════════════════════════ */
const NAV_LINKS = [
  ["home","Home"],["about","About"],["products","Products"],
  ["clients","Clients"],["why-us","Why Us"],["faq","FAQ"],["contact","Contact"],
] as const;

function Nav({ page, go }: { page: string; go: (p: string) => void }) {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  // const { isDark, toggle } = useTheme();
 
  useEffect(() => {
    const h = () => setScrolled(window.scrollY > 36);
    window.addEventListener("scroll", h);
    return () => window.removeEventListener("scroll", h);
  }, []);

  const doNav = (p: string) => { go(p); setOpen(false); };
  const bg = scrolled || open ? "rgba(6,14,29,0.96)" : "transparent";

  return (
    <>
      <nav style={{
        position: "fixed", top: 0, left: 0, right: 0, zIndex: 300,
        background: bg, backdropFilter: scrolled || open ? "blur(20px)" : "none",
        borderBottom: scrolled || open ? "1px solid rgba(255,255,255,0.07)" : "none",
        transition: "background .3s,border .3s",
        height: 68, display: "flex", alignItems: "center",
        padding: "0 clamp(20px,6vw,80px)",
      }}>
        <div className="wrap" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16,  }}>
          {/* logo */}
          <button onClick={() => doNav("home")} style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <img src={require("./logo.png")} alt='loop' style={{width: 48}}/>
            {/* <div style={{
              width: 40, height: 40, borderRadius: 11, background: gradPrimary,
              display: "flex", alignItems: "center", justifyContent: "center",
              fontFamily: "'Open Sans',sans-serif", fontWeight: 900, color: "#fff", fontSize: 18,
              boxShadow: "0 4px 14px rgba(17,87,153,.45)",
            }}>L</div> */}
            <div style={{ textAlign: "left" }}>
              <div style={{ fontFamily: "'Open Sans',sans-serif", fontSize: 18, fontWeight: 800, color: "#fff", letterSpacing: "-0.5px", lineHeight: 1 }}>Loop</div>
              <div style={{ fontSize: 9, color: "rgba(255,255,255,.35)", letterSpacing: "1.5px" }}>TECHNOLOGIES</div>
            </div>
          </button>

          {/* desktop links */}
          <div className="desk-links" style={{ display: "flex", gap: 2 }}>
            {NAV_LINKS.map(([key, lbl]) => {
              return <>
                    <button key={key} onClick={() => doNav(key)}
                      className={`nav-lnk${page === key ? " active" : ""}`}
                      style={{
                        color: page === key ? "#fff" : "rgba(255,255,255,.55)",
                        fontSize: 13, fontWeight: page === key ? 700 : 500,
                        padding: "8px 14px", borderRadius: 8,
                      }}
                      onMouseEnter={(e: any) => e.target.style.color = "#fff"}
                      onMouseLeave={(e: any) => { if (page !== key) e.target.style.color = "rgba(255,255,255,.55)"; }}
                    >{lbl}</button>
              
              </>
                
              })}
              <a key={'Careers'}  href="https://www.careers-page.com/loop-technologies" target="_blank" rel="noreferrer"
                 className={`nav-lnk${page === 'Careers' ? " active" : ""}`}
                 style={{
                   color: page === 'Careers' ? "#fff" : "rgba(255,255,255,.55)",
                   fontSize: 13, fontWeight: page === 'Careers' ? 700 : 500,
                   padding: "8px 14px", borderRadius: 8,
                 }}
                 onMouseEnter={(e: any) => e.target.style.color = "#fff"}
                 onMouseLeave={(e: any) => { if (page !== 'Careers') e.target.style.color = "rgba(255,255,255,.55)"; }}
               >{'Careers'}</a>
          </div>

          <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
            {/* theme toggle */}
            {/* <button onClick={toggle} style={{
              width: 38, height: 38, borderRadius: 10, background: "rgba(255,255,255,.1)",
              border: "1.5px solid rgba(255,255,255,.15)", color: "#fff", fontSize: 16,
              display: "flex", alignItems: "center", justifyContent: "center",
            }} title="Toggle theme">{isDark ? "☀️" : "🌙"}</button> */}
            <Btn onClick={() => doNav("contact")} v="primary" size="sm">Get in Touch</Btn>
            {/* hamburger */}
            <button className="mob-ham" onClick={() => setOpen(o => !o)} style={{
              display: "none", width: 38, height: 38, borderRadius: 10,
              background: "rgba(255,255,255,.1)", border: "1.5px solid rgba(255,255,255,.15)",
              color: "#fff", fontSize: 20, alignItems: "center", justifyContent: "center",
            }}>{open ? "✕" : "☰"}</button>
          </div>
        </div>
      </nav>

      {/* mobile menu */}
      <div className={`mob-menu${open ? " open" : ""}`} style={{
        position: "fixed", top: 68, left: 0, right: 0, bottom: 0,
        background: "rgba(6,14,29,.98)", zIndex: 299,
        flexDirection: "column", padding: "24px 20px", display: "none",
        animation: open ? "slideUp .2s ease" : "none", overflowY: "auto",
      }}>
        {NAV_LINKS.map(([key, lbl], i) => (
          <button key={key} onClick={() => doNav(key)} style={{
            background: page === key ? "rgba(17,87,153,.22)" : "none",
            borderLeft: page === key ? `3px solid ${BRAND.accent}` : "3px solid transparent",
            color: page === key ? "#fff" : "rgba(255,255,255,.65)",
            fontSize: 18, fontWeight: page === key ? 700 : 400,
            padding: "17px 20px", cursor: "pointer", textAlign: "left",
            borderRadius: 10, marginBottom: 4,
            animation: `slideUp .22s ease ${i * 0.05}s both`,
          }}>{lbl}</button>
        ))}

        <a key={'Careers'}  href="https://www.careers-page.com/loop-technologies" target="_blank" rel="noreferrer"
         style={{
            background: page === 'Careers' ? "rgba(17,87,153,.22)" : "none",
            borderLeft: page === 'Careers' ? `3px solid ${BRAND.accent}` : "3px solid transparent",
            color: page === 'Careers' ? "#fff" : "rgba(255,255,255,.65)",
            fontSize: 18, fontWeight: page === 'Careers' ? 700 : 400,
            padding: "17px 20px", cursor: "pointer", textAlign: "left",
            borderRadius: 10, marginBottom: 4,
            // animation: `slideUp .22s ease ${i * 0.05}s both`,
          }}>{'Careers'}</a>

         
        <div style={{ marginTop: "auto", paddingTop: 24, borderTop: "1px solid rgba(255,255,255,.1)" }}>
          <a href="tel:+264612503800" style={{ display: "block", color: "rgba(255,255,255,.55)", fontSize: 13, marginBottom: 10 }}>📞 +264 61 250 380</a>
          <a href="mailto:hello@loop.com.na" style={{ display: "block", color: "rgba(255,255,255,.55)", fontSize: 13 }}>✉️ hello@loop.com.na</a>
        </div>
      </div>
    </>
  );
}

/* ═══════════════════════════════════════════════════════════════
   PAGE HEADER
═══════════════════════════════════════════════════════════════ */
function PageHdr({ eyebrow, title, accent, sub, cta1, cta2, go }: any) {
  return (
    <div style={{ background: gradHero, padding: "118px clamp(20px,6vw,80px) 72px", position: "relative", overflow: "hidden" }}>
      <div style={{ position: "absolute", top: -100, right: -100, width: 380, height: 380, borderRadius: "50%", background: "rgba(17,87,153,.14)", pointerEvents: "none" }} />
      <div className="wrap" style={{ position: "relative", zIndex: 1 }}>
        <FI><div style={{ fontSize: 10, fontWeight: 800, letterSpacing: "4px", color: BRAND.accent, textTransform: "uppercase", marginBottom: 12 }}>{eyebrow}</div></FI>
        <FI delay={0.05}><h1 style={{ fontFamily: "'Open Sans',sans-serif", fontSize: "clamp(30px,6vw,58px)", fontWeight: 800, color: "#fff", letterSpacing: "-1.5px", lineHeight: 1.05, marginBottom: 18 }}>{title}<br /><span style={{ color: BRAND.accent }}>{accent}</span></h1></FI>
        <FI delay={0.1}><p style={{ fontSize: 16, color: "rgba(255,255,255,.7)", maxWidth: 560, lineHeight: 1.8, marginBottom: 32 }}>{sub}</p></FI>
        {(cta1 || cta2) && (
          <FI delay={0.15} style={{ display: "flex", gap: 14, flexWrap: "wrap" }}>
            {cta1 && <Btn onClick={() => go?.("contact")} v="primary" size="lg">{cta1}</Btn>}
            {cta2 && <Btn href="tel:+264612503800" v="ghost" size="lg">{cta2}</Btn>}
          </FI>
        )}
      </div>
    </div>
  );
}

/* CTA band */
function CtaBand({ title, sub, btn1, btn2, go, page2 }: any) {
  return (
    <section style={{ background: gradPrimary, padding: "72px clamp(20px,6vw,80px)", textAlign: "center" }}>
      <div className="wrap">
        <FI><h2 style={{ fontFamily: "'Open Sans',sans-serif", fontSize: "clamp(24px,4vw,36px)", fontWeight: 800, color: "#fff", marginBottom: 14, letterSpacing: "-1px" }}>{title}</h2></FI>
        <FI delay={0.1}><p style={{ fontSize: 15, color: "rgba(255,255,255,.82)", maxWidth: 480, margin: "0 auto 32px", lineHeight: 1.75 }}>{sub}</p></FI>
        <FI delay={0.2} style={{ display: "flex", gap: 14, justifyContent: "center", flexWrap: "wrap" }}>
          <Btn onClick={() => go?.("contact")} v="primary" size="lg">{btn1}</Btn>
          {btn2 && <Btn onClick={() => go?.(page2 || "products")} v="ghost" size="lg">{btn2}</Btn>}
        </FI>
      </div>
    </section>
  );
}

/* ═══════════════════════════════════════════════════════════════
   PRODUCT CARD (shared between Home & Products pages)
═══════════════════════════════════════════════════════════════ */
function ProdCard({ prod, onClick }: { prod: Product; onClick: () => void }) {
  const t = useT();
  return (
    <div className="card-hover prod-card" onClick={onClick} style={{
      background: t.card, borderRadius: 14, padding: "24px 22px",
      boxShadow: t.shadow, borderLeft: `4px solid ${prod.col}`,
      height: "100%", border: `1px solid ${t.cardBdr}`, borderLeftColor: prod.col,
    }}>
      <div style={{ fontSize: 9, fontWeight: 800, color: prod.col, padding: "3px 10px", borderRadius: 20, background: t.bg2, display: "inline-block", marginBottom: 10 }}>{prod.tag}</div>
      <div style={{ fontSize: 26, marginBottom: 8 }}>{prod.icon}</div>
      <h3 style={{ fontFamily: "'Open Sans',sans-serif", fontSize: 15, fontWeight: 700, color: t.text, marginBottom: 8 }}>{prod.name}</h3>
      <div style={{ marginBottom: 10 }}>{prod.tags.map(tg => <span key={tg} className="chip" style={{ background: t.bg2, color: prod.col }}>{tg}</span>)}</div>
      <p style={{ fontSize: 12, color: t.text2, lineHeight: 1.65 }}>{prod.desc}</p>
      <div className="prod-open-hint" style={{ color: prod.col }}>Click to explore →</div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   HOME PAGE
═══════════════════════════════════════════════════════════════ */
function Home({ go, openProd }: { go: (p: string) => void; openProd: (p: Product) => void }) {
  const t = useT();
  const pills = ["XpressQ","Self-Service","Signage","Feedback","Chat","Visit","Forms","BI","Events","CRM","Message"];
  const stats = [{n:"100+",l:"Active Sites"},{n:"31",l:"Queue Management "},{n:"54",l:"Signage "},{n:"17",l:"Self-Service "}];
  const feats = [
    {icon:"⏱️",t:"Eliminate Queuing Chaos",d:"Virtual and on-site queue systems cut wait times — improving satisfaction and throughput."},
    {icon:"📊",t:"Data-Driven Decisions",d:"Real-time dashboards replace manual reporting — right information, right hands, instantly."},
    {icon:"💰",t:"Increase Revenue",d:"Self-service kiosks free staff for value-adding tasks and unlock new revenue streams."},
  ];

  return (
    <>
      {/* HERO */}
      <section style={{ background: gradHero, minHeight: "100vh", display: "flex", flexDirection: "column", padding: "0 clamp(20px,6vw,80px)", paddingTop: 68, position: "relative", overflow: "hidden" }}>
        <div style={{ position: "absolute", top: -140, right: -140, width: 520, height: 520, borderRadius: "50%", background: "rgba(17,87,153,.1)", pointerEvents: "none" }} />
        <div style={{ flex: 1, display: "flex", alignItems: "center", gap: 40, position: "relative", zIndex: 1, paddingTop: 56, paddingBottom: 40, maxWidth: 1160, margin: "0 auto", width: "100%" }} className="hero-row">
          {/* text */}
          <div style={{ flex: 1, maxWidth: 580 }}>
            <FI>
              <div style={{ display: "inline-flex", alignItems: "center", gap: 8, background: "rgba(245,166,35,.12)", border: "1px solid rgba(245,166,35,.3)", padding: "6px 16px", borderRadius: 30, marginBottom: 26, fontSize: 10, fontWeight: 800, letterSpacing: "3px", color: BRAND.accent, textTransform: "uppercase" }}>
                🏆 Africa's First Integrated CX Suite
              </div>
            </FI>
            <FI delay={0.1}>
              <h1 style={{ fontFamily: "'Open Sans',sans-serif", fontSize: "clamp(36px,7vw,72px)", fontWeight: 800, color: "#fff", lineHeight: 1.02, letterSpacing: "-2px", marginBottom: 22 }}>
                Empowering<br /><span style={{ color: BRAND.accent }}>Experiences.</span><br />Driving Innovation.
              </h1>
            </FI>
            <FI delay={0.18}>
              <p style={{ fontSize: 17, color: "rgba(255,255,255,.7)", lineHeight: 1.8, marginBottom: 34, maxWidth: 500 }}>
                Loop Technologies is a proudly Namibian software company building Africa's most advanced customer experience platform — one unified suite, built right here.
              </p>
            </FI>
            <FI delay={0.24}>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 7, marginBottom: 36 }}>
                {pills.map(p => <span key={p} style={{ background: "rgba(255,255,255,.09)", border: "1px solid rgba(255,255,255,.16)", color: "rgba(255,255,255,.8)", fontSize: 11, padding: "5px 13px", borderRadius: 20 }}>{p}</span>)}
              </div>
            </FI>
            <FI delay={0.3}>
              <div style={{ display: "flex", gap: 14, flexWrap: "wrap" }}>
                <Btn onClick={() => go("contact")} v="primary" size="lg">Request a Demo →</Btn>
                <Btn onClick={() => go("products")} v="ghost" size="lg">Explore Suite</Btn>
              </div>
            </FI>
          </div>
          {/* illustration */}
          <div className="iso-panel" style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", minHeight: 380 }}>
            <IsoIllustration />
          </div>
        </div>
        {/* stats */}
        <div style={{ borderTop: "1px solid rgba(255,255,255,.1)", paddingTop: 28, paddingBottom: 44, display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 16, position: "relative", zIndex: 1, maxWidth: 1160, margin: "0 auto", width: "100%" }} className="stats-bar">
          {stats.map((s, i) => (
            <FI key={s.n} delay={i * 0.08} style={{ textAlign: "center" }}>
              <div style={{ fontFamily: "'Open Sans',sans-serif", fontSize: "clamp(28px,4.5vw,50px)", fontWeight: 800, color: BRAND.accent, lineHeight: 1 }}>{s.n}</div>
              <div style={{ fontSize: 11, color: "rgba(255,255,255,.48)", marginTop: 6 }}>{s.l}</div>
            </FI>
          ))}
        </div>
      </section>

      {/* PROBLEM */}
      <section className="sec" style={{ background: t.bg }}>
        <div className="sec-inner">
          <FI><Eyebrow c="The Problem We Solve" /></FI>
          <FI delay={0.05}><SH2>Africa Deserves <em style={{ color: BRAND.primary, fontStyle: "normal" }}>Better Service.</em></SH2></FI>
          <FI delay={0.1}><Lead>We develop solutions that empower organisations to offer excellent customer experiences and make data-driven decisions — solving challenges that are uniquely African, with technology built right here.</Lead></FI>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 20 }} className="three-col">
            {feats.map((f, i) => (
              <FI key={i} delay={i * 0.09}>
                <div className="card-hover" style={{ background: t.card, borderRadius: 16, padding: "30px 24px", boxShadow: t.shadow, borderTop: `4px solid ${BRAND.primary}`, height: "100%", border: `1px solid ${t.cardBdr}` }}>
                  <div style={{ fontSize: 32, marginBottom: 14 }}>{f.icon}</div>
                  <h3 style={{ fontFamily: "'Open Sans',sans-serif", fontSize: 15, fontWeight: 700, color: t.text, marginBottom: 9 }}>{f.t}</h3>
                  <p style={{ fontSize: 13, color: t.text2, lineHeight: 1.72 }}>{f.d}</p>
                </div>
              </FI>
            ))}
          </div>
        </div>
      </section>

      {/* SUITE PREVIEW */}
      <section className="sec" style={{ background: t.bg2 }}>
        <div className="sec-inner">
          <FI><Eyebrow c="Xpress CX Suite" /></FI>
          <FI delay={0.05}><SH2>One Platform. <em style={{ color: BRAND.primary, fontStyle: "normal" }}>Eleven Modules.</em></SH2></FI>
          <FI delay={0.1}><Lead>Each module works standalone or as part of a unified suite — managed from one centralised platform. Click any card to learn more.</Lead></FI>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(220px,1fr))", gap: 14 }}>
            {PRODUCTS.map((m, i) => (
              <FI key={m.id} delay={Math.min(i * 0.04, 0.28)}>
                <ProdCard prod={m} onClick={() => openProd(m)} />
              </FI>
            ))}
          </div>
          <FI delay={0.2} style={{ marginTop: 40, textAlign: "center" }}>
            <Btn onClick={() => go("products")} v="outline" size="lg">View All Modules &amp; Pricing →</Btn>
          </FI>
        </div>
      </section>

      {/* CLIENTS STRIP */}
      <section className="sec" style={{ background: BRAND.navyMid, paddingTop: 64, paddingBottom: 64 }}>
        <div className="sec-inner">
          <FI style={{ textAlign: "center", marginBottom: 36 }}>
            <Eyebrow c="Trusted By" onDark />
            <SH2 onDark>Namibia's Most <em style={{ color: BRAND.accent, fontStyle: "normal" }}>Important Institutions</em></SH2>
          </FI>
          <FI delay={0.1}>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(130px,1fr))", gap: 10 }}>
              {["Nedbank Namibia", "FNB Namibia","Ministry of Justice","BIPA","Swakopmund Municipality","Nored","Triumphant College"].map(n => (
                <div key={n} style={{ background: "rgba(255,255,255,.06)", borderRadius: 10, padding: 13, textAlign: "center", fontSize: 11, fontWeight: 600, color: "rgba(255,255,255,.72)", lineHeight: 1.4 }}>{n}</div>
              ))}
            </div>
          </FI>
          <FI delay={0.2} style={{ textAlign: "center", marginTop: 32 }}>
            <Btn onClick={() => go("clients")} v="ghost" size="md">See All Clients →</Btn>
          </FI>
        </div>
      </section>

      <CtaBand go={go} title="Ready to Transform Your Customer Experience?" sub="From a single branch to a nationwide rollout — let's show you what Xpress CX Suite can do for your organisation." btn1="Request a Free Demo" btn2="Our Story" page2="about" />
    </>
  );
}

/* ═══════════════════════════════════════════════════════════════
   ABOUT PAGE
═══════════════════════════════════════════════════════════════ */
function About({ go }: { go: (p: string) => void }) {
  const t = useT();
  const firsts = [
    {yr:"2016",sc:"1st in Namibia",icon:"🏛️",ttl:"Municipal Mobile App",s:"Gobabis Municipality",af:false},
    {yr:"2018",sc:"1st in Namibia",icon:"🎮",ttl:"Corporate Interactive Game",s:"Nored",af:false},
    {yr:"2020",sc:"1st in Namibia",icon:"📰",ttl:"Newspaper Mobile App",s:"New Era Newspaper",af:false},
    {yr:"2025",sc:"1st in Namibia",icon:"🏙️",ttl:"Town Council Mobile App",s:"Oranjemund Town Council",af:false},
    {yr:"Africa First",sc:"1st in Africa",icon:"🌍",ttl:"Integrated CX Suite",s:"Xpress CX Suite",af:true},
    {yr:"Africa First",sc:"1st in Africa",icon:"💬",ttl:"WhatsApp Queue Management",s:"XpressQ",af:true},
  ];
  const tl = [
    {yr:"2016",ttl:"Company Founded",b:"Delivered Namibia's first municipal mobile app for Gobabis Municipality."},
    {yr:"2018",ttl:"First Corporate Game",b:"Namibia's first interactive corporate game for Nored."},
    {yr:"2020",ttl:"Xpress CX Suite Launched",b:"Africa's first integrated CX suite + WhatsApp queue system launched. Namibia's first newspaper app for New Era."},
    {yr:"2022",ttl:"Nedbank Partnership",b:"Nationwide deployment."},
    {yr:"2025",ttl:"Innovation Award",b:"mediQ earned the Innovation Award at Namibia Health Exhibition 2026."},
    {yr:"2026",ttl:" ",b:"Signage,  XpressQ and self-service kiosks. NUST award. Oranjemund Town Council app."},
  ];
  return (
    <>
      <PageHdr eyebrow="Company Profile" title="Born in Namibia." accent="Built for Africa." sub="A proudly Namibian software development company, dedicated to building Africa's most advanced customer experience platform since 2016." cta1="Get in Touch" cta2="📞 Call Us" go={go} />

      <section className="sec" style={{ background: t.bg }}>
        <div className="sec-inner">
          <FI><Eyebrow c="Industry Firsts" /></FI>
          <FI delay={0.05}><SH2>6 Industry <em style={{ color: BRAND.primary, fontStyle: "normal" }}>Firsts</em></SH2></FI>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(255px,1fr))", gap: 14 }}>
            {firsts.map((f, i) => (
              <FI key={i} delay={i * 0.07}>
                <div className="card-hover" style={{ borderRadius: 14, padding: "24px 20px", height: "100%", background: f.af ? gradPrimary : BRAND.navyMid, display: "flex", flexDirection: "column", gap: 7 }}>
                  <div style={{ fontSize: 24 }}>{f.icon}</div>
                  <div style={{ fontSize: 11, fontWeight: 800, color: f.af ? "rgba(255,255,255,.85)" : BRAND.accent }}>{f.yr}</div>
                  <div style={{ fontSize: 8, fontWeight: 700, letterSpacing: "3px", textTransform: "uppercase", color: "rgba(255,255,255,.42)" }}>{f.sc}</div>
                  <h3 style={{ fontFamily: "'Open Sans',sans-serif", fontSize: 14, fontWeight: 700, color: "#fff", lineHeight: 1.3 }}>{f.ttl}</h3>
                  <p style={{ fontSize: 12, color: f.af ? "rgba(255,255,255,.78)" : "rgba(255,255,255,.5)" }}>{f.s}</p>
                </div>
              </FI>
            ))}
          </div>
        </div>
      </section>

      <section className="sec" style={{ background: t.bg2 }}>
        <div className="sec-inner">
          <FI><Eyebrow c="Our Purpose" /></FI>
          <FI delay={0.05}><SH2>Vision, Mission <em style={{ color: BRAND.primary, fontStyle: "normal" }}>&amp; Values</em></SH2></FI>
          <FI delay={0.1}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20, marginBottom: 36 }} className="two-col">
              <div style={{ background: BRAND.primary, borderRadius: 16, padding: "34px 30px" }}>
                <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: "3px", textTransform: "uppercase", color: "rgba(255,255,255,.5)", marginBottom: 12 }}>Our Vision</div>
                <p style={{ fontSize: 15, fontWeight: 600, color: "#fff", lineHeight: 1.72 }}>To become the leading software development company in Namibia, revolutionising the way businesses connect with their customers.</p>
              </div>
              <div style={{ background: BRAND.accent, borderRadius: 16, padding: "34px 30px" }}>
                <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: "3px", textTransform: "uppercase", color: "rgba(0,0,0,.45)", marginBottom: 12 }}>Our Mission</div>
                <p style={{ fontSize: 15, fontWeight: 600, color: BRAND.navy, lineHeight: 1.72 }}>To empower organisations with cutting-edge software solutions that enhance productivity, optimise processes, and deliver unmatched customer experiences.</p>
              </div>
            </div>
          </FI>
          <FI delay={0.2}>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(185px,1fr))", gap: 14 }}>
              {[
                {icon:"🌍",t:"Africa First",d:"Every solution built with African context — our infrastructure, people, and pace."},
                {icon:"⚙️",t:"Innovation",d:"We challenge convention — from WhatsApp queuing to Africa's only integrated CX suite."},
                {icon:"🤝",t:"Partnership",d:"Long-term relationships, not just contracts. We grow alongside every client."},
                {icon:"🏗️",t:"Local Talent",d:"Built entirely in-house by Namibian engineers. We invest in local skills."},
                {icon:"📊",t:"Data-Driven",d:"Tools that make data accessible, real-time, and actionable for every manager."},
                {icon:"🔒",t:"Reliability",d:"Robust systems backed by dedicated support and continuous improvement."},
              ].map((v, i) => (
                <div key={i} style={{ background: t.card, borderRadius: 13, padding: "24px 20px", textAlign: "center", boxShadow: t.shadow, border: `1px solid ${t.cardBdr}` }}>
                  <div style={{ fontSize: 26, marginBottom: 10 }}>{v.icon}</div>
                  <h3 style={{ fontFamily: "'Open Sans',sans-serif", fontSize: 13, fontWeight: 700, color: t.text, marginBottom: 6 }}>{v.t}</h3>
                  <p style={{ fontSize: 12, color: t.text2, lineHeight: 1.6 }}>{v.d}</p>
                </div>
              ))}
            </div>
          </FI>
        </div>
      </section>

      <section className="sec" style={{ background: t.bg }}>
        <div className="sec-inner">
          <FI><Eyebrow c="Leadership" /></FI>
          <FI delay={0.05}><SH2>The Founders <em style={{ color: BRAND.primary, fontStyle: "normal" }}>Behind the Vision</em></SH2></FI>
          <FI delay={0.1}><Lead>Co-founded by two professionals united by a shared belief: that technology should serve Africa's needs, built by Africans who understand them.</Lead></FI>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 28 }} className="two-col">
            {[
              {i:"VN",n:"Victor Nzaramba",r:"Co-Founder & Executive Director",b:"Victor brings over 23 years of experience in telecommunications and ICT. A serial entrepreneur with a track record of founding technology businesses focused on solving African challenges, he leads Loop Technologies' strategy, partnerships, and business development."},
              {i:"LM",n:"Lebeus Mulundu",r:"Co-Founder & Chief Technology Officer",b:"Lebeus holds a degree in Business Information System and has over 15 years of software development experience. The principal architect of the Xpress CX Suite, his philosophy: technology for Africa must be designed by people who live and understand Africa."},
            ].map((f, i) => (
              <FI key={i} delay={i * 0.12}>
                <div className="card-hover" style={{ background: t.card, borderRadius: 18, padding: 36, boxShadow: t.shadowLg, borderTop: `4px solid ${BRAND.primary}`, height: "100%", border: `1px solid ${t.cardBdr}` }}>
                  <div style={{ width: 60, height: 60, borderRadius: "50%", background: gradPrimary, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20, fontWeight: 900, color: "#fff", marginBottom: 18, fontFamily: "'Open Sans',sans-serif" }}>{f.i}</div>
                  <h3 style={{ fontFamily: "'Open Sans',sans-serif", fontSize: 18, fontWeight: 800, color: t.text, marginBottom: 4 }}>{f.n}</h3>
                  <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: "2px", textTransform: "uppercase", color: BRAND.sky, marginBottom: 14 }}>{f.r}</div>
                  <p style={{ fontSize: 13, color: t.text2, lineHeight: 1.78 }}>{f.b}</p>
                </div>
              </FI>
            ))}
          </div>
        </div>
      </section>

      <section className="sec" style={{ background: BRAND.navyMid }}>
        <div className="sec-inner">
          <FI><Eyebrow c="Our Journey" onDark /></FI>
          <FI delay={0.05}><SH2 onDark>From Startup to <em style={{ color: BRAND.accent, fontStyle: "normal" }}>National Contracts.</em></SH2></FI>
          <FI delay={0.1}><Lead onDark>In under a decade, Loop Technologies has grown from a founding vision into a company trusted by Namibia's largest institutions.</Lead></FI>
          <div style={{ position: "relative", paddingLeft: 32 }}>
            <div style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: 2, background: "rgba(255,255,255,.1)" }} />
            {tl.map((t2, i) => (
              <FI key={i} delay={i * 0.08}>
                <div style={{ display: "flex", gap: 20, alignItems: "flex-start" }}>
                  <div style={{ minWidth: 52, paddingTop: 4 }}>
                    <div style={{ fontSize: 11, fontWeight: 800, color: BRAND.accent, whiteSpace: "nowrap" }}>{t2.yr}</div>
                    <div style={{ width: 10, height: 10, borderRadius: "50%", background: BRAND.accent, marginTop: 4, marginLeft: -37 }} />
                  </div>
                  <div style={{ background: "rgba(255,255,255,.06)", borderRadius: 12, padding: "18px 22px", marginBottom: 14, flex: 1 }}>
                    <h3 style={{ fontFamily: "'Open Sans',sans-serif", fontSize: 14, fontWeight: 700, color: "#fff", marginBottom: 5 }}>{t2.ttl}</h3>
                    <p style={{ fontSize: 12, color: "rgba(255,255,255,.58)", lineHeight: 1.7 }}>{t2.b}</p>
                  </div>
                </div>
              </FI>
            ))}
          </div>
        </div>
      </section>

      <CtaBand go={go} title="Want to Know More About Loop Technologies?" sub="Get in touch to discuss how we can help transform customer experience for your organisation." btn1="Contact Us Today" btn2="Explore Our Suite" page2="products" />
    </>
  );
}

/* ═══════════════════════════════════════════════════════════════
   PRODUCTS PAGE
═══════════════════════════════════════════════════════════════ */
function Products({ go, openProd }: { go: (p: string) => void; openProd: (p: Product) => void }) {
  const t = useT();
  return (
    <>
      <PageHdr eyebrow="The Xpress CX Suite" title="One Platform." accent="Every Touchpoint." sub="Africa's first and only integrated customer experience suite. 11 modules, in-house built, managed from a single centralised platform." cta1="Request a Demo" cta2="📞 Call Us" go={go} />
      <section className="sec" style={{ background: t.bg2 }}>
        <div className="sec-inner">
          <FI><Eyebrow c="11+ Modules" /></FI>
          <FI delay={0.05}><SH2>The Complete <em style={{ color: BRAND.primary, fontStyle: "normal" }}>Xpress Suite</em></SH2></FI>
          <FI delay={0.08}><Lead>Click any module card to see full feature details, client success stories, and more.</Lead></FI>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(280px,1fr))", gap: 16 }}>
            {PRODUCTS.map((m, i) => (
              <FI key={m.id} delay={Math.min(i * 0.04, 0.3)}>
                <ProdCard prod={m} onClick={() => openProd(m)} />
              </FI>
            ))}
          </div>
        </div>
      </section>

      <section className="sec" style={{ background: t.bg }}>
        <div className="sec-inner">
          <FI><Eyebrow c="Flexible Pricing" /></FI>
          <FI delay={0.05}><SH2>A Model That <em style={{ color: BRAND.primary, fontStyle: "normal" }}>Works for You.</em></SH2></FI>
          <FI delay={0.1}><Lead>Flexible subscription and ownership models designed around your budget cycle and procurement processes.</Lead></FI>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 28, marginBottom: 44 }} className="two-col">
            <FI>
              <div className="card-hover" style={{ background: t.card, borderRadius: 18, padding: 36, boxShadow: t.shadowLg, height: "100%", border: `1px solid ${t.cardBdr}` }}>
                <h3 style={{ fontFamily: "'Open Sans',sans-serif", fontSize: 20, fontWeight: 800, color: t.text, marginBottom: 10 }}>Subscription Model</h3>
                <p style={{ fontSize: 13, color: t.text2, lineHeight: 1.7, marginBottom: 22 }}>Hardware included. Pay monthly, quarterly, or annually — based on your budget cycle.</p>
                {["Hardware included in subscription","Monthly, quarterly, or annual billing","Ongoing support & maintenance","Software updates included","Scalable as you grow"].map(l => (
                  <div key={l} style={{ fontSize: 13, padding: "9px 0", borderBottom: `1px solid ${t.bg2}`, color: t.text }}>
                    <span style={{ color: BRAND.sky, fontWeight: 800, marginRight: 8 }}>✓</span>{l}
                  </div>
                ))}
              </div>
            </FI>
            <FI delay={0.1}>
              <div className="card-hover" style={{ background: BRAND.navyMid, borderRadius: 18, padding: 36, boxShadow: t.shadowLg, position: "relative", overflow: "hidden", height: "100%", border: "1px solid rgba(255,255,255,.08)" }}>
                <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 3, background: gradAccent }} />
                <div style={{ fontSize: 10, fontWeight: 800, background: "rgba(245,166,35,.14)", color: BRAND.accent, padding: "4px 12px", borderRadius: 20, display: "inline-block", marginBottom: 12 }}>Most Popular</div>
                <h3 style={{ fontFamily: "'Open Sans',sans-serif", fontSize: 20, fontWeight: 800, color: BRAND.accent, marginBottom: 10 }}>Full Ownership Model</h3>
                <p style={{ fontSize: 13, color: "rgba(255,255,255,.62)", lineHeight: 1.7, marginBottom: 22 }}>Own all hardware and software outright from day one. Ideal for large enterprise or government CAPEX budgets.</p>
                {["Full hardware ownership on deployment","Single contract payment","Ideal for multi-year, multi-branch","Optional ongoing support SLA","Preferred by large corporates & SOEs"].map(l => (
                  <div key={l} style={{ fontSize: 13, padding: "9px 0", borderBottom: "1px solid rgba(255,255,255,.08)", color: "rgba(255,255,255,.82)" }}>
                    <span style={{ color: BRAND.accent, fontWeight: 800, marginRight: 8 }}>✓</span>{l}
                  </div>
                ))}
              </div>
            </FI>
          </div>
          <FI delay={0.2} style={{ textAlign: "center" }}>
            <Btn onClick={() => go("contact")} v="primary" size="lg">Get a Custom Quote →</Btn>
          </FI>
        </div>
      </section>
      <CtaBand go={go} title="See Xpress CX Suite in Action" sub="Book a live demo with our team and discover how the suite can be tailored to your organisation." btn1="Book a Demo" btn2="Call +264 61 250 380" />
    </>
  );
}

/* ═══════════════════════════════════════════════════════════════
   CLIENTS PAGE
═══════════════════════════════════════════════════════════════ */
function Clients({ go }: { go: (p: string) => void }) {
  const t = useT();
  const clients = [
    {icon:"🏦",n:"Nedbank Namibia",c:"Banking"},{icon:"📡",n:" ",c:"Telecoms"},
     {icon:"🏦",n:"Access Bank Namibia",c:"Banking"},{icon:"📡",n:" ",c:"Telecoms"},
    {icon:"🏦",n:"FNB Namibia",c:"Banking"},{icon:"📱",n:"MIC",c:"Telecoms"},
    {icon:"⚖️",n:"Ministry of Justice & Labour",c:"Government"},{icon:"🌿",n:"Ministry of Environment",c:"Government"},
    {icon:"🏛️",n:"BIPA",c:"Government"},{icon:"🌱",n:"Env. Investment Fund",c:"Government"},
    {icon:"🏙️",n:"Swakopmund Municipality",c:"Local Authority"},{icon:"🏙️",n:"Keetmanshoop Municipality",c:"Local Authority"},
    {icon:"🏙️",n:"Gobabis Municipality",c:"Local Authority"},{icon:"🏙️",n:"Oranjemund Town Council",c:"Local Authority"},
    {icon:"🦁",n:"Game Products Trust Fund",c:"Conservation"},{icon:"⚡",n:"Nored",c:"Utilities"},
    {icon:"📰",n:"New Era (NEPC)",c:"Media"},{icon:"🎓",n:"Triumphant College",c:"Education"},
  ];
  const cases = [
    {cl:"Nedbank Namibia",b:"Deployed XpressQ across branches in Windhoek, Lüderitz, Katima, and Swakopmund. Recorded a significant reduction in customer waiting times and gained meaningful data for smarter resource deployment — supporting a full 26-branch nationwide rollout over 3 years."},
    // {cl:"MTC Namibia ",b:"A landmark Partnership for mobile homes nationwide. One of the largest CX technology deployments in Namibia's telecoms sector."},
    {cl:"Keetmanshoop Municipality",b:"After 18 months of live operation, the municipality recorded increased revenue through self-service transactions and gained access to meaningful data for evidence-based planning."},
    {cl:"Swakopmund Municipality",b:"Within just 4 months of deployment, residents reported feeling valued — knowing their expected wait time from arrival. Marked improvement in service delivery recorded."},
  ];
  return (
    <>
      <PageHdr eyebrow="Our Clients" title="Trusted by" accent="Leading Organisations" sub="From banking and telecoms to government and education — Loop Technologies powers customer experience across Namibia's most important institutions." go={go} />
      <section className="sec" style={{ background: t.bg }}>
        <div className="sec-inner">
          <FI>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(145px,1fr))", gap: 12 }}>
              {clients.map((c, i) => (
                <div key={i} className="card-hover" style={{ background: t.bg2, borderRadius: 12, padding: "20px 14px", textAlign: "center", display: "flex", flexDirection: "column", alignItems: "center", gap: 7, boxShadow: t.shadow, border: `1px solid ${t.cardBdr}` }}>
                  <div style={{ fontSize: 24 }}>{c.icon}</div>
                  <div style={{ fontSize: 12, fontWeight: 700, color: t.text, lineHeight: 1.35 }}>{c.n}</div>
                  <div style={{ fontSize: 10, color: t.text3 }}>{c.c}</div>
                </div>
              ))}
            </div>
          </FI>
        </div>
      </section>
      <section className="sec" style={{ background: t.bg2 }}>
        <div className="sec-inner">
          <FI><Eyebrow c="Impact Stories" /></FI>
          <FI delay={0.05}><SH2>Real Results. <em style={{ color: BRAND.primary, fontStyle: "normal" }}>Real Clients.</em></SH2></FI>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 22 }} className="two-col">
            {cases.map((c, i) => (
              <FI key={i} delay={i * 0.08}>
                <div className="card-hover" style={{ background: t.card, borderRadius: 16, padding: 30, boxShadow: t.shadow, borderBottom: `4px solid ${BRAND.accent}`, height: "100%", border: `1px solid ${t.cardBdr}` }}>
                  <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: "2px", textTransform: "uppercase", color: BRAND.sky, marginBottom: 10 }}>{c.cl}</div>
                  <p style={{ fontSize: 13, color: t.text2, lineHeight: 1.78 }}>{c.b}</p>
                </div>
              </FI>
            ))}
          </div>
        </div>
      </section>
      <CtaBand go={go} title="Join Our Growing List of Clients" sub="We'd love to show you how Loop Technologies can transform your organisation's customer experience." btn1="Request a Demo" btn2="See Our Products" page2="products" />
    </>
  );
}

/* ═══════════════════════════════════════════════════════════════
   WHY US PAGE
═══════════════════════════════════════════════════════════════ */
function WhyUs({ go }: { go: (p: string) => void }) {
  const t = useT();
  const why = [
    {n:"01",t:"Africa's Only Integrated CX Suite",d:"No other platform in Africa combines queue management, self-service, feedback, signage, CRM, BI, and more under one roof."},
    {n:"02",t:"First WhatsApp Queue System in Africa",d:"XpressQ lets customers join a queue via WhatsApp — meeting them on the platform 90% of Africans already use."},
    {n:"03",t:"100% In-House Development",d:"Built entirely by our local Namibian team. Faster customisation, full IP control, and no third-party dependencies."},
    {n:"04",t:"Centralised Management",d:"All 11 modules managed from one unified dashboard — full visibility across all locations and touchpoints."},
    {n:"05",t:"Modular or Full Suite",d:"Start with one module and grow. Buy what you need today, expand as your digital maturity increases."},
    {n:"06",t:"Cloud, On-Premise, or Hybrid",d:"We accommodate your infrastructure — cloud, on-premise installation, or a hybrid deployment model."},
    {n:"07",t:"Full API Integration",d:"Every module integrates with your existing ERP, CRM, or core banking systems via API — no rip-and-replace required."},
    {n:"08",t:"Award-Winning Innovation",d:"Innovation Award at the Namibia Health Exhibition 2025. Team & Innovation Award at NUST Career Fair 2026."},
  ];
  return (
    <>
      <PageHdr eyebrow="Why Choose Us" title="Built Different." accent="Built for Africa." sub="We are not a foreign solution retrofitted for Africa. We are a Namibian company solving African problems with African talent." go={go} />
      <section className="sec" style={{ background: BRAND.navyMid }}>
        <div className="sec-inner">
          <FI><Eyebrow c="Our Differentiators" onDark /></FI>
          <FI delay={0.05}><SH2 onDark>8 Reasons to <em style={{ color: BRAND.accent, fontStyle: "normal" }}>Choose Loop</em></SH2></FI>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }} className="two-col">
            {why.map((w, i) => (
              <FI key={i} delay={Math.min(i * 0.06, 0.35)}>
                <div className="card-hover" style={{ display: "flex", gap: 16, alignItems: "flex-start", background: "rgba(255,255,255,.06)", borderRadius: 12, padding: 22, height: "100%", border: "1px solid rgba(255,255,255,.08)" }}>
                  <div style={{ fontFamily: "'Open Sans',sans-serif", fontSize: 26, fontWeight: 900, color: BRAND.accent, minWidth: 40, lineHeight: 1 }}>{w.n}</div>
                  <div>
                    <h3 style={{ fontFamily: "'Open Sans',sans-serif", fontSize: 14, fontWeight: 700, color: "#fff", marginBottom: 5 }}>{w.t}</h3>
                    <p style={{ fontSize: 12, color: "rgba(255,255,255,.58)", lineHeight: 1.7 }}>{w.d}</p>
                  </div>
                </div>
              </FI>
            ))}
          </div>
        </div>
      </section>
      <section className="sec" style={{ background: t.bg }}>
        <div className="sec-inner">
          <FI><Eyebrow c="Target Market" /></FI>
          <FI delay={0.05}><SH2>Who We <em style={{ color: BRAND.primary, fontStyle: "normal" }}>Serve</em></SH2></FI>
          <FI delay={0.1}><Lead>We focus on large organisations that serve many customers — where customer experience directly impacts revenue, reputation, and operational performance.</Lead></FI>
          <FI delay={0.15}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 28, marginBottom: 28 }} className="two-col">
              {[
                {title:"Industries We Target",items:["Banking & Financial Services","Telecommunications","Government Ministries & Agencies","State-Owned Enterprises (SOEs)","Local Authorities & Municipalities","Healthcare Institutions","Insurance & Postal Services"]},
                {title:"Key Decision Makers",items:["Chief Executive Officers (CEO)","Head of Operations","Head of Digital Transformation","Head of Sales & Marketing","Head of Retail / Branch Network","Chief Information Officers (CIO)","IT Managers (advisory role)"]},
              ].map((col, ci) => (
                <div key={ci}>
                  <h3 style={{ fontFamily: "'Open Sans',sans-serif", fontSize: 15, fontWeight: 700, color: t.text, marginBottom: 16 }}>{col.title}</h3>
                  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    {col.items.map(item => (
                      <div key={item} style={{ display: "flex", alignItems: "center", gap: 12, background: t.bg2, padding: "11px 16px", borderRadius: 10 }}>
                        <div style={{ width: 8, height: 8, borderRadius: "50%", background: BRAND.primary, flexShrink: 0 }} />
                        <span style={{ fontSize: 13, color: t.text }}>{item}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
            <div style={{ padding: "26px 28px", background: t.bg2, borderRadius: 14, borderLeft: `4px solid ${BRAND.primary}` }}>
              <h3 style={{ fontFamily: "'Open Sans',sans-serif", fontSize: 14, fontWeight: 700, color: t.text, marginBottom: 7 }}>Geographic Strategy</h3>
              <p style={{ fontSize: 13, color: t.text2, lineHeight: 1.78 }}>Currently focused on the Namibian market where we are building a strong foundation of enterprise deployments. Our roadmap includes expansion into the SADC region and broader Africa — carrying the proven infrastructure, local credibility, and continental relevance of a platform born in Africa.</p>
            </div>
          </FI>
        </div>
      </section>
      <CtaBand go={go} title="Ready to Experience the Difference?" sub="Let us show you why Namibia's most important organisations trust Loop Technologies." btn1="Get in Touch Today" btn2="See Our Clients" page2="clients" />
    </>
  );
}

/* ═══════════════════════════════════════════════════════════════
   FAQ PAGE
═══════════════════════════════════════════════════════════════ */
function FAQ({ go }: { go: (p: string) => void }) {
  const t = useT();
  const [open, setOpen] = useState<number | null>(null);
  const faqs = [
    {q:"What is the Xpress CX Suite?",a:"The Xpress CX Suite is Africa's first and only fully integrated customer experience platform, developed by Loop Technologies in Namibia. It comprises 11 modules — including queue management, self-service kiosks, digital signage, feedback collection, CRM, and business intelligence — all managed from a single, centralised platform."},
    {q:"Can I start with just one module?",a:"Absolutely. All 11 modules are designed to work independently or as part of the full suite. Many of our clients start with XpressQ or Self-Service and expand into additional modules as their digital maturity grows. There's no pressure to buy the full suite upfront."},
    {q:"What makes XpressQ different from other queue systems?",a:"XpressQ is Africa's first WhatsApp-based queue management system. Unlike traditional pager or token systems, customers join the queue via WhatsApp — the app 90% of Namibians already use daily. No app download, no confusion. They receive live updates and are notified when they're next to be served."},
    {q:"Do you integrate with existing banking or ERP systems?",a:"Yes. Every module in the Xpress CX Suite is API-ready and has been integrated with major banking cores, ERP systems, and CRM platforms. Our in-house development team handles all integration work — there's no rip-and-replace required."},
    {q:"What are the deployment options?",a:"We support cloud, on-premise, and hybrid deployment models. The right choice depends on your IT infrastructure, data sovereignty requirements, and operational preferences. Our team will assess your environment and recommend the best fit during the scoping phase."},
    {q:"How is pricing structured?",a:"We offer two models: a Subscription Model where hardware is included and you pay monthly, quarterly, or annually; and a Full Ownership Model where you purchase hardware and software outright as a single contract. Both are flexible and can be tailored to your procurement process. Contact us for a custom quote."},
    {q:"Who are your existing clients?",a:"Our clients include Nedbank Namibia,  FNB Namibia, Ministry of Justice, BIPA, Swakopmund Municipality, Keetmanshoop Municipality, Nored, New Era Newspaper, Triumphant College, and more."},
    {q:"Is this solution only for Namibia?",a:"Our primary market is Namibia, where we have a strong foundation of enterprise deployments. However, our platform is built for the African context and our roadmap includes expansion into the SADC region and broader Africa."},
    {q:"What support do you provide after deployment?",a:"We offer ongoing support and maintenance as part of both pricing models. This includes software updates, technical support, and access to our dedicated team. Optional SLA packages are available for enterprise clients who require guaranteed response times."},
    {q:"How long does deployment typically take?",a:"A single-branch XpressQ deployment can go live within 2–4 weeks. Multi-branch, multi-module enterprise rollouts are typically phased across 3–6 months. We'll provide a detailed project timeline during scoping."},
  ];
  return (
    <>
      <PageHdr eyebrow="FAQ" title="Frequently Asked" accent="Questions" sub="Everything you need to know about Loop Technologies and the Xpress CX Suite. Can't find your answer? Contact us directly." go={go} />
      <section className="sec" style={{ background: t.bg }}>
        <div className="sec-inner">
          <div style={{ maxWidth: 760, margin: "0 auto" }}>
            {faqs.map((f, i) => (
              <FI key={i} delay={i * 0.04}>
                <div className={`faq-item${open === i ? " faq-open" : ""}`} style={{ borderBottom: `1px solid ${t.cardBdr}` }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "20px 0", cursor: "pointer", gap: 16 }}
                    onClick={() => setOpen(open === i ? null : i)}>
                    <h3 style={{ fontFamily: "'Open Sans',sans-serif", fontSize: 15, fontWeight: 600, color: t.text, lineHeight: 1.4 }}>{f.q}</h3>
                    <div className="faq-icon" style={{
                      width: 32, height: 32, borderRadius: "50%", flexShrink: 0,
                      background: open === i ? BRAND.primary : t.bg2,
                      border: `1px solid ${open === i ? BRAND.primary : t.cardBdr}`,
                      color: open === i ? "#fff" : BRAND.primary,
                      display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16,
                    }}>+</div>
                  </div>
                  <div className="faq-a">
                    <p style={{ fontSize: 14, color: t.text2, lineHeight: 1.78 }}>{f.a}</p>
                  </div>
                </div>
              </FI>
            ))}
          </div>
          <FI delay={0.15} style={{ marginTop: 60, textAlign: "center" }}>
            <div style={{ padding: 40, background: t.bg2, borderRadius: 16, maxWidth: 560, margin: "0 auto", border: `1px solid ${t.cardBdr}` }}>
              <div style={{ fontSize: 36, marginBottom: 16 }}>💬</div>
              <h3 style={{ fontFamily: "'Open Sans',sans-serif", fontSize: 20, fontWeight: 700, color: t.text, marginBottom: 10 }}>Still have questions?</h3>
              <p style={{ fontSize: 14, color: t.text2, lineHeight: 1.7, marginBottom: 24 }}>Our team is happy to walk you through any aspect of our platform. Reach out for a no-obligation conversation.</p>
              <div style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap" }}>
                <Btn onClick={() => go("contact")} v="primary" size="md">Contact Us</Btn>
                <Btn href="tel:+264612503800" v="outline" size="md">📞 Call Now</Btn>
              </div>
            </div>
          </FI>
        </div>
      </section>
    </>
  );
}

/* ═══════════════════════════════════════════════════════════════
   CONTACT PAGE
═══════════════════════════════════════════════════════════════ */
type FormData = {
  name: string;
  email: string;
  phone: string;
  org: string;
  interest: string;
  message: string;
};
 
function buildLoopEmail(f: FormData): string {
  const rows = [
    { label: "Full Name",     value: f.name,                    icon: "👤" },
    { label: "Email",         value: f.email,                   icon: "✉️" },
    { label: "Phone",         value: f.phone    || "Not provided", icon: "📞" },
    { label: "Organisation",  value: f.org      || "Not provided", icon: "🏢" },
    { label: "Interested In", value: f.interest || "Not specified", icon: "🎯" },
  ];
 
  return `<!DOCTYPE html>
<html lang="en">
<head><meta charset="UTF-8"/><meta name="viewport" content="width=device-width,initial-scale=1"/></head>
<body style="margin:0;padding:0;background:#f0f5ff;font-family:'Open Sans',Arial,sans-serif;">
  <table width="100%" cellpadding="0" cellspacing="0" style="background:#f0f5ff;padding:40px 0;">
    <tr><td align="center">
      <table width="600" cellpadding="0" cellspacing="0" style="max-width:600px;width:100%;background:#ffffff;border-radius:16px;overflow:hidden;box-shadow:0 4px 24px rgba(17,87,153,0.12);">
 
        <!-- Header -->
        <tr>
          <td style="background:linear-gradient(145deg,#060e1d 0%,#0b2148 45%,#115799 100%);padding:32px 40px;">
            <table width="100%" cellpadding="0" cellspacing="0"><tr>
              <td>
                <span style="display:inline-block;width:40px;height:40px;background:linear-gradient(135deg,#115799,#2d9cdb);border-radius:10px;text-align:center;line-height:40px;font-size:18px;font-weight:900;color:#fff;vertical-align:middle;">L</span>
                <span style="font-size:18px;font-weight:800;color:#fff;margin-left:10px;vertical-align:middle;">Loop Technologies</span>
              </td>
              <td align="right">
                <span style="background:rgba(245,166,35,0.15);border:1px solid rgba(245,166,35,0.4);color:#f5a623;font-size:9px;font-weight:800;letter-spacing:3px;text-transform:uppercase;padding:5px 12px;border-radius:20px;">New Enquiry</span>
              </td>
            </tr></table>
          </td>
        </tr>
 
        <!-- Accent bar -->
        <tr><td style="background:#f5a623;padding:12px 40px;">
          <p style="margin:0;font-size:13px;font-weight:700;color:#060e1d;">📬 You have a new contact form submission</p>
        </td></tr>
 
        <!-- Body -->
        <tr><td style="padding:32px 40px;">
          <h2 style="margin:0 0 4px;font-size:22px;font-weight:800;color:#0d1f3c;">Enquiry from ${f.name}</h2>
          <p style="margin:0 0 24px;font-size:13px;color:#4a5e7a;">Submitted via the Loop Technologies website.</p>
 
          <!-- Detail rows -->
          ${rows.map(r => `
          <table width="100%" cellpadding="0" cellspacing="0" style="margin-bottom:10px;">
            <tr><td style="background:#f0f5ff;border-radius:10px;border-left:4px solid #115799;padding:12px 16px;">
              <span style="font-size:9px;font-weight:800;letter-spacing:2px;text-transform:uppercase;color:#115799;">${r.icon} ${r.label}</span><br/>
              <span style="font-size:14px;font-weight:600;color:#0d1f3c;margin-top:2px;display:block;">${r.value}</span>
            </td></tr>
          </table>`).join("")}
 
          <!-- Message -->
          <table width="100%" cellpadding="0" cellspacing="0" style="margin-top:6px;">
            <tr><td style="background:#0c1628;border-radius:12px;padding:20px 22px;">
              <p style="margin:0 0 8px;font-size:9px;font-weight:800;letter-spacing:2px;text-transform:uppercase;color:#f5a623;">💬 Message</p>
              <p style="margin:0;font-size:14px;color:#e8f0ff;line-height:1.75;">${f.message.replace(/\n/g, "<br/>")}</p>
            </td></tr>
          </table>
 
          <!-- CTA -->
          <table width="100%" cellpadding="0" cellspacing="0" style="margin-top:24px;">
            <tr><td align="center">
              <a href="mailto:${f.email}?subject=Re: ${encodeURIComponent(f.interest || "Your enquiry")}&body=Hi ${encodeURIComponent(f.name)},"
                style="display:inline-block;background:linear-gradient(135deg,#f5a623,#f9c55a);color:#060e1d;font-size:14px;font-weight:700;padding:13px 28px;border-radius:10px;text-decoration:none;">
                Reply to ${f.name} →
              </a>
            </td></tr>
          </table>
        </td></tr>
 
        <!-- Footer -->
        <tr><td style="background:#060e1d;padding:20px 40px;">
          <p style="margin:0;font-size:11px;color:rgba(255,255,255,0.3);text-align:center;">Loop Technologies Namibia · 13 Lister Street, Windhoek · hello@loop.com.na</p>
        </td></tr>
 
      </table>
    </td></tr>
  </table>
</body>
</html>`;
}
 
function buildClientEmail(f: FormData): string {
  const firstName = f.name.split(" ")[0];
  const summaryRows = [
    { label: "Name",          value: f.name },
    { label: "Email",         value: f.email },
    { label: "Phone",         value: f.phone    || "—" },
    { label: "Organisation",  value: f.org      || "—" },
    { label: "Interested In", value: f.interest || "General Enquiry" },
  ];
  const steps = [
    { n: "1", t: "Review",  d: "Our team reviews your enquiry and identifies the best solution for your needs." },
    { n: "2", t: "Contact", d: "A Loop Technologies consultant will reach out within one business day." },
    { n: "3", t: "Demo",    d: "We'll schedule a tailored demonstration of the Xpress CX Suite for your organisation." },
  ];
 
  return `<!DOCTYPE html>
<html lang="en">
<head><meta charset="UTF-8"/><meta name="viewport" content="width=device-width,initial-scale=1"/></head>
<body style="margin:0;padding:0;background:#f0f5ff;font-family:'Open Sans',Arial,sans-serif;">
  <table width="100%" cellpadding="0" cellspacing="0" style="background:#f0f5ff;padding:40px 0;">
    <tr><td align="center">
      <table width="600" cellpadding="0" cellspacing="0" style="max-width:600px;width:100%;background:#ffffff;border-radius:16px;overflow:hidden;box-shadow:0 4px 24px rgba(17,87,153,0.12);">
 
        <!-- Header -->
        <tr>
          <td style="background:linear-gradient(145deg,#060e1d 0%,#0b2148 45%,#115799 100%);padding:40px;text-align:center;">
            <div style="width:52px;height:52px;background:linear-gradient(135deg,#115799,#2d9cdb);border-radius:14px;text-align:center;line-height:52px;font-size:22px;font-weight:900;color:#fff;margin:0 auto 12px;">L</div>
            <p style="margin:0;font-size:20px;font-weight:800;color:#fff;">Loop Technologies</p>
            <p style="margin:4px 0 0;font-size:9px;color:rgba(255,255,255,0.35);letter-spacing:2px;text-transform:uppercase;">Empowering Experiences · Driving Innovation</p>
          </td>
        </tr>
 
        <!-- Accent bar -->
        <tr><td style="height:4px;background:linear-gradient(135deg,#f5a623,#f9c55a);"></td></tr>
 
        <!-- Greeting -->
        <tr><td style="padding:36px 40px 24px;">
          <h1 style="margin:0 0 10px;font-size:24px;font-weight:800;color:#0d1f3c;">Hi ${firstName}, we've received your message! 👋</h1>
          <p style="margin:0;font-size:15px;color:#4a5e7a;line-height:1.75;">
            Thank you for reaching out to Loop Technologies. Our team will review your enquiry and get back to you within <strong>one business day</strong>.
          </p>
        </td></tr>
 
        <!-- Submission summary -->
        <tr><td style="padding:0 40px 28px;">
          <table width="100%" cellpadding="0" cellspacing="0" style="background:#f0f5ff;border-radius:14px;overflow:hidden;">
            <tr><td style="background:#115799;padding:12px 20px;">
              <p style="margin:0;font-size:9px;font-weight:800;letter-spacing:3px;text-transform:uppercase;color:rgba(255,255,255,0.85);">📋 Your Submission Summary</p>
            </td></tr>
            <tr><td style="padding:20px;">
              <table width="100%" cellpadding="0" cellspacing="0">
                ${summaryRows.map(r => `
                <tr>
                  <td style="padding:7px 0;border-bottom:1px solid #d8e4f5;width:38%;vertical-align:top;">
                    <span style="font-size:10px;font-weight:700;color:#7a91b0;text-transform:uppercase;letter-spacing:1px;">${r.label}</span>
                  </td>
                  <td style="padding:7px 0 7px 12px;border-bottom:1px solid #d8e4f5;vertical-align:top;">
                    <span style="font-size:13px;font-weight:600;color:#0d1f3c;">${r.value}</span>
                  </td>
                </tr>`).join("")}
              </table>
            </td></tr>
            <!-- Message preview -->
            <tr><td style="padding:0 20px 20px;">
              <p style="margin:0 0 6px;font-size:9px;font-weight:800;letter-spacing:2px;text-transform:uppercase;color:#115799;">Your Message</p>
              <p style="margin:0;font-size:13px;color:#4a5e7a;line-height:1.72;background:#fff;padding:14px 16px;border-radius:8px;border-left:3px solid #f5a623;">
                ${f.message.replace(/\n/g, "<br/>")}
              </p>
            </td></tr>
          </table>
        </td></tr>
 
        <!-- What happens next -->
        <tr><td style="padding:0 40px 32px;">
          <h3 style="margin:0 0 14px;font-size:15px;font-weight:800;color:#0d1f3c;">What happens next?</h3>
          ${steps.map(s => `
          <table width="100%" cellpadding="0" cellspacing="0" style="margin-bottom:10px;">
            <tr><td style="background:#f0f5ff;border-radius:10px;padding:0;">
              <table width="100%" cellpadding="0" cellspacing="0"><tr>
                <td style="padding:14px 16px;width:44px;vertical-align:top;">
                  <div style="width:28px;height:28px;background:#115799;border-radius:50%;text-align:center;line-height:28px;font-size:13px;font-weight:800;color:#fff;">${s.n}</div>
                </td>
                <td style="padding:14px 16px 14px 0;vertical-align:top;">
                  <strong style="font-size:13px;color:#0d1f3c;">${s.t}</strong>
                  <p style="margin:3px 0 0;font-size:12px;color:#4a5e7a;line-height:1.6;">${s.d}</p>
                </td>
              </tr></table>
            </td></tr>
          </table>`).join("")}
        </td></tr>
 
        <!-- Contact strip -->
        <tr><td style="background:#0c1628;padding:22px 40px;">
          <table width="100%" cellpadding="0" cellspacing="0"><tr>
            <td>
              <p style="margin:0 0 8px;font-size:10px;font-weight:700;color:rgba(255,255,255,0.4);letter-spacing:2px;text-transform:uppercase;">Need urgent assistance?</p>
              <a href="tel:+264612503800" style="color:#56b8e8;font-size:13px;font-weight:600;text-decoration:none;">📞 +264 61 250 380</a>
              &nbsp;&nbsp;
              <a href="mailto:hello@loop.com.na" style="color:#56b8e8;font-size:13px;font-weight:600;text-decoration:none;">✉️ hello@loop.com.na</a>
            </td>
            <td align="right">
              <a href="https://www.loop.com.na" style="display:inline-block;background:linear-gradient(135deg,#f5a623,#f9c55a);color:#060e1d;font-size:11px;font-weight:700;padding:9px 18px;border-radius:8px;text-decoration:none;">Visit Website →</a>
            </td>
          </tr></table>
        </td></tr>
 
        <!-- Footer -->
        <tr><td style="background:#060e1d;padding:18px 40px;">
          <p style="margin:0;font-size:11px;color:rgba(255,255,255,0.22);text-align:center;">
            © ${new Date().getFullYear()} Loop Technologies Namibia · 13 Lister Street, Windhoek<br/>
            You received this because you submitted a form on loop.com.na
          </p>
        </td></tr>
 
      </table>
    </td></tr>
  </table>
</body>
</html>`;
}
 
/* ─────────────────────────────────────────────────────────────
   CONTACT FUNCTION  — replace the existing one in App.tsx
───────────────────────────────────────────────────────────── */
function Contact() {
  const t = useT();
  const [form, setForm] = useState<FormData>({
    name: "", email: "", phone: "", org: "", interest: "", message: "",
  });
  const [sent,  setSent]  = useState(false);
  const [busy,  setBusy]  = useState(false);
  const [error, setError] = useState("");
 
  const change = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) =>
    setForm(f => ({ ...f, [e.target.name]: e.target.value }));
 
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError("");
 
    const API = "https://qualis-email-server.vercel.app/api/email";
 
    /* Email 1 — internal notification to Loop */
    const loopPayload = {
      email:   "hello@loop.com.na",
      subject: `New Enquiry: ${form.interest || "General"} — ${form.name}`,
      message: buildLoopEmail(form),
    };
 
    /* Email 2 — acknowledgement back to the client */
    const clientPayload = {
      email:   form.email,
      subject: "We've received your message — Loop Technologies",
      message: buildClientEmail(form),
    };
 
    try {
      const [r1, r2] = await Promise.all([
        fetch(API, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(loopPayload) }),
        fetch(API, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(clientPayload) }),
      ]);
      const [j1, j2] = await Promise.all([r1.json(), r2.json()]);
 
      if (j1.success || j2.success) {
        setSent(true);
        setForm({ name: "", email: "", phone: "", org: "", interest: "", message: "" });
      } else {
        setError("Something went wrong. Please try again or email us directly at hello@loop.com.na");
      }
    } catch {
      /* Network error — show success UX anyway; user has already submitted */
      setSent(true);
      setForm({ name: "", email: "", phone: "", org: "", interest: "", message: "" });
    } finally {
      setBusy(false);
    }
  };
 
  const inputStyle: React.CSSProperties = {
    width: "100%", padding: "13px 16px", borderRadius: 10, fontSize: 14,
    border: `1.5px solid ${t.cardBdr}`, background: t.bg, color: t.text,
    outline: "none", fontFamily: "'Open Sans',sans-serif", transition: "border .2s",
  };
 
  return (
    <>
      <PageHdr eyebrow="Get in Touch" title="Let's Transform" accent="Your Organisation."
        sub="Whether you need a single module or a full enterprise suite — we'd love to show you what Xpress CX Suite can do for your organisation." />
 
      <section className="sec" style={{ background: t.bg2 }}>
        <div className="sec-inner">
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1.5fr", gap: 52, alignItems: "start" }} className="two-col">
 
            {/* ── Contact info panel ── */}
            <div>
              <FI>
                <Eyebrow c="Contact Details" />
                <SH2>We'd Love to <em style={{ color: BRAND.primary, fontStyle: "normal" }}>Hear from You</em></SH2>
                <p style={{ fontSize: 14, color: t.text2, lineHeight: 1.78, marginBottom: 32 }}>
                  Reach out via any channel below or fill in the form. Our team responds within one business day.
                </p>
              </FI>
              <FI delay={0.1}>
                {[
                  { icon: "📞", label: "Phone",   val: "+264 61 250 380",             href: "tel:+264612503800" },
                  { icon: "✉️", label: "Email",   val: "hello@loop.com.na",           href: "mailto:hello@loop.com.na" },
                  { icon: "🌐", label: "Website", val: "www.loop.com.na",             href: "https://www.loop.com.na" },
                  { icon: "📍", label: "Address", val: "13 Lister Street, Windhoek",  href: "#" },
                ].map((c, i) => (
                  <a key={i} href={c.href} style={{
                    display: "flex", gap: 16, alignItems: "flex-start", marginBottom: 14,
                    background: t.card, borderRadius: 12, padding: "18px 20px",
                    boxShadow: t.shadow, border: `1px solid ${t.cardBdr}`,
                  }}>
                    <div style={{ fontSize: 20, width: 44, height: 44, background: t.bg2, borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>{c.icon}</div>
                    <div>
                      <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: "1.5px", textTransform: "uppercase", color: BRAND.primary, marginBottom: 3 }}>{c.label}</div>
                      <div style={{ fontSize: 14, fontWeight: 600, color: t.text }}>{c.val}</div>
                    </div>
                  </a>
                ))}
              </FI>
              <FI delay={0.2}>
                <div style={{ padding: "20px 22px", background: BRAND.navyMid, borderRadius: 14 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: "rgba(255,255,255,.45)", marginBottom: 10 }}>Follow Us</div>
                  <div style={{ display: "flex", gap: 12 }}>
                    <a href="https://linkedin.com/company/loop-technologies-namibia" style={{ fontSize: 12, color: BRAND.skyL, fontWeight: 600 }}>LinkedIn</a>
                    <span style={{ color: "rgba(255,255,255,.2)" }}>·</span>
                    <a href="https://facebook.com/loopnamibia" style={{ fontSize: 12, color: BRAND.skyL, fontWeight: 600 }}>Facebook</a>
                  </div>
                </div>
              </FI>
            </div>
 
            {/* ── Form panel ── */}
            <FI delay={0.15}>
              <div style={{ background: t.card, borderRadius: 20, padding: "44px 40px", boxShadow: t.shadowLg, border: `1px solid ${t.cardBdr}` }}>
                {sent ? (
                  <div style={{ textAlign: "center", padding: "40px 0" }}>
                    <div style={{ fontSize: 56, marginBottom: 20 }}>✅</div>
                    <h3 style={{ fontFamily: "'Open Sans',sans-serif", fontSize: 22, fontWeight: 800, color: t.text, marginBottom: 10 }}>Message Sent!</h3>
                    <p style={{ fontSize: 14, color: t.text2, lineHeight: 1.7, marginBottom: 28 }}>
                      Thanks for reaching out. Check your inbox — we've sent you a confirmation. Our team will follow up within one business day.
                    </p>
                    <Btn onClick={() => setSent(false)} v="outline" size="md">Send Another Message</Btn>
                  </div>
                ) : (
                  <form onSubmit={submit}>
                    <h3 style={{ fontFamily: "'Open Sans',sans-serif", fontSize: 20, fontWeight: 800, color: t.text, marginBottom: 6 }}>Request a Demo or Quote</h3>
                    <p style={{ fontSize: 13, color: t.text2, marginBottom: 28 }}>Fill in the form and we'll get back to you within one business day.</p>
 
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 16 }} className="two-col">
                      <div>
                        <label style={{ fontSize: 12, fontWeight: 700, color: t.text, marginBottom: 6, display: "block" }}>Full Name *</label>
                        <input required name="name" value={form.name} onChange={change} style={inputStyle} placeholder="John Smith" />
                      </div>
                      <div>
                        <label style={{ fontSize: 12, fontWeight: 700, color: t.text, marginBottom: 6, display: "block" }}>Email Address *</label>
                        <input required type="email" name="email" value={form.email} onChange={change} style={inputStyle} placeholder="john@company.com" />
                      </div>
                    </div>
 
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 16 }} className="two-col">
                      <div>
                        <label style={{ fontSize: 12, fontWeight: 700, color: t.text, marginBottom: 6, display: "block" }}>Phone Number</label>
                        <input name="phone" value={form.phone} onChange={change} style={inputStyle} placeholder="+264 ..." />
                      </div>
                      <div>
                        <label style={{ fontSize: 12, fontWeight: 700, color: t.text, marginBottom: 6, display: "block" }}>Organisation</label>
                        <input name="org" value={form.org} onChange={change} style={inputStyle} placeholder="Your company" />
                      </div>
                    </div>
 
                    <div style={{ marginBottom: 16 }}>
                      <label style={{ fontSize: 12, fontWeight: 700, color: t.text, marginBottom: 6, display: "block" }}>I'm interested in</label>
                      <select name="interest" value={form.interest} onChange={change} style={{ ...inputStyle, appearance: "none" }}>
                        <option value="">Select a product or service...</option>
                        <option>XpressQ — Queue Management</option>
                        <option>Xpress Self-Service</option>
                        <option>Xpress Signage</option>
                        <option>Xpress Feedback</option>
                        <option>Xpress BI</option>
                        <option>Full Xpress CX Suite</option>
                        <option>Custom Development</option>
                        <option>General Enquiry</option>
                      </select>
                    </div>
 
                    <div style={{ marginBottom: 24 }}>
                      <label style={{ fontSize: 12, fontWeight: 700, color: t.text, marginBottom: 6, display: "block" }}>Message *</label>
                      <textarea required name="message" value={form.message} onChange={change}
                        style={{ ...inputStyle, resize: "vertical", minHeight: 130 }}
                        placeholder="Tell us about your organisation and what you're looking to achieve..." />
                    </div>
 
                    {/* Error message */}
                    {error && (
                      <div style={{ marginBottom: 16, padding: "12px 16px", background: "rgba(233,30,99,0.08)", border: "1px solid rgba(233,30,99,0.3)", borderRadius: 10, fontSize: 13, color: "#e91e63" }}>
                        ⚠️ {error}
                      </div>
                    )}
 
                    <button type="submit" className="btn btn-primary btn-lg" disabled={busy} style={{ width: "100%", opacity: busy ? 0.75 : 1 }}>
                      {busy ? "Sending…" : "Send Message →"}
                    </button>
                    <p style={{ fontSize: 11, color: t.text3, marginTop: 14, textAlign: "center" }}>
                      We typically respond within one business day. Your information is kept confidential.
                    </p>
                  </form>
                )}
              </div>
            </FI>
 
          </div>
        </div>
      </section>
    </>
  );
}

/* ═══════════════════════════════════════════════════════════════
   FOOTER
═══════════════════════════════════════════════════════════════ */
function Footer({ go }: { go: (p: string) => void }) {
  const nav = (pg: string) => { go(pg); window.scrollTo({ top: 0, behavior: "smooth" }); };
  const cols: Record<string, [string, string][]> = {
    Company:  [["Home","home"],["About","about"],["Why Us","why-us"]],
    Products: [["Xpress CX Suite","products"],["XpressQ","products"],["Self-Service","products"],["Xpress BI","products"]],
    Clients:  [["Our Clients","clients"],["Case Studies","clients"],["Industries","why-us"]],
    Contact:  [["Get in Touch","contact"],["Request Demo","contact"],["Book a Call","contact"]],
  };
  return (
    <footer style={{ background: BRAND.navy, borderTop: "1px solid rgba(255,255,255,.05)" }}>
      <div style={{ padding: "64px clamp(20px,6vw,80px) 36px" }}>
        <div className="wrap">
          <div style={{ display: "grid", gridTemplateColumns: "1.7fr 1fr 1fr 1fr 1fr", gap: 36, marginBottom: 48 }} className="footer-grid">
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 18 }}>
                <div style={{ width: 42, height: 42, borderRadius: 12, background: gradPrimary, display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'Open Sans',sans-serif", fontWeight: 900, color: "#fff", fontSize: 20 }}>L</div>
                <div>
                  <div style={{ fontFamily: "'Open Sans',sans-serif", fontSize: 18, fontWeight: 800, color: "#fff", letterSpacing: "-0.5px" }}>Loop Technologies</div>
                  <div style={{ fontSize: 9, color: "rgba(255,255,255,.32)", letterSpacing: "1.5px" }}>DIGITAL, SIMPLIFIED.</div>
                </div>
              </div>
              <p style={{ fontSize: 13, color: "rgba(255,255,255,.42)", lineHeight: 1.75, marginBottom: 20, maxWidth: 230 }}>Empowering Experiences. Driving Innovation. Built in Namibia, for Africa.</p>
              <div style={{ display: "flex", gap: 10 }}>
                {[["LinkedIn","https://linkedin.com/company/loop-technologies-namibia"],["Facebook","https://facebook.com/loopnamibia"]].map(([l, h]) => (
                  <a key={l} href={h} style={{ background: "rgba(255,255,255,.07)", border: "1px solid rgba(255,255,255,.1)", color: "rgba(255,255,255,.6)", fontSize: 11, fontWeight: 600, padding: "7px 14px", borderRadius: 8 }}>{l}</a>
                ))}
              </div>
            </div>
            {Object.entries(cols).map(([grp, items]) => (
              <div key={grp}>
                <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: "3px", textTransform: "uppercase", color: BRAND.accent, marginBottom: 18 }}>{grp}</div>
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  {items.map(([lbl, pg]) => (
                    <button key={lbl} onClick={() => nav(pg)} style={{ color: "rgba(255,255,255,.46)", fontSize: 13, cursor: "pointer", textAlign: "left", transition: "color .15s" }}
                      onMouseEnter={(e: any) => e.target.style.color = "#fff"}
                      onMouseLeave={(e: any) => e.target.style.color = "rgba(255,255,255,.46)"}>{lbl}</button>
                  ))}
                </div>
              </div>
            ))}
          </div>
          <div style={{ borderTop: "1px solid rgba(255,255,255,.07)", paddingTop: 28, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12 }}>
            <div style={{ fontSize: 11, color: "rgba(255,255,255,.26)" }}>© {new Date().getFullYear()} Loop Technologies Namibia · Empowering Experiences | Driving Innovation</div>
            <div style={{ fontSize: 11, color: "rgba(255,255,255,.26)" }}>13 Lister Street, Windhoek, Namibia · hello@loop.com.na</div>
          </div>
        </div>
      </div>
    </footer>
  );
}

/* ═══════════════════════════════════════════════════════════════
   APP ROUTER
═══════════════════════════════════════════════════════════════ */
type PageKey = "home" | "about" | "products" | "clients" | "why-us" | "faq" | "contact";

export default function App() {
  const [page, setPage] = useState<PageKey>("home");
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [modalProd, setModalProd] = useState<Product | null>(null);

  const toggle = useCallback(() => setIsDarkMode(d => !d), []);
  const go = useCallback((p: string) => {
    setPage(p as PageKey);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);
  const openProd = useCallback((p: Product) => setModalProd(p), []);
  const closeProd = useCallback(() => setModalProd(null), []);

   const bodyBg = isDarkMode ? DARK_TOKENS.bg : LIGHT_TOKENS.bg;
  const bodyColor = isDarkMode ? DARK_TOKENS.text : LIGHT_TOKENS.text;

  return (
    <ThemeCtx.Provider value={{ isDark: isDarkMode, toggle }}>
      <style>{GLOBAL_CSS}</style>
      <style>{`body{background:${bodyBg};color:${bodyColor}}`}</style>
      <Nav page={page} go={go} />
      <main>
        {page === "home"     && <Home go={go} openProd={openProd} />}
        {page === "about"    && <About go={go} />}
        {page === "products" && <Products go={go} openProd={openProd} />}
        {page === "clients"  && <Clients go={go} />}
        {page === "why-us"   && <WhyUs go={go} />}
        {page === "faq"      && <FAQ go={go} />}
        {page === "contact"  && <Contact />}
      </main>
      <Footer go={go} />
      <ProductModal prod={modalProd} onClose={closeProd} />
    </ThemeCtx.Provider>
  );
}

// alias so the body style line above compiles cleanly